import * as swcHelpers from '@swc/helpers';
import { mergeLabels, useIntl, useFeatureToggles, useDispatch, useSelector } from '@grandvisionhq/state';
import { resilientFetch, getDisplayName, isString, getLocalizedValue, QueryString, compose, LocaleUtils, StringUtils, getAttrValue, getLocalizedAttrValue, sanitizeStringForUrl, withLazyLoading } from '@grandvisionhq/utils';
import { FacetId, useAlgoliaConfig, AlgoliaSearch, SearchPageHeader, ConnectedProductListing, isValidAlgoliaSearchConfig, QueryAlgoliaProducts } from '@grandvisionhq/search';
import * as jsonpointer from 'jsonpointer';
import { MediaQuery, AccordionItem, RichTextArea, Accordion, Button, toast, SliderCarousel, Carousel, IFrame, ContentTabs, Breadcrumbs, NotificationBar as NotificationBar$1, Olapic as Olapic$1, ScrollUpButton, SpotlightedStores as SpotlightedStores$1, CartButton } from '@grandvisionhq/common';
import { initialize, isContainerItem } from '@bloomreach/spa-sdk';
import * as React from 'react';
import React__default from 'react';
import { BrPageContext, BrComponentContext, BrManageContentButton, BrComponent, BrManageMenuButton, BrPage } from '@bloomreach/react-sdk';
import { CommerceToolsApi } from '@grandvisionhq/commercetools';
import { gql, useMutation } from '@grandvisionhq/graphql';
import { Banner, HighlightedContentItem, HighlightedContent, RichBanner, ContentBanner, PromotionBannersItem, CategoryLinksItem, CertificatesItem, PromotionBanners, CategoryLinks, Certificates, BlogList, BlogCard, BlogHero, ExclusiveBrandsItem, ExclusiveBrands, BrandListItem, BrandList as BrandList$1, BrandListGroup, Video, FaqItem, Faq as Faq$1, HeadTags, IconList as IconList$1, Newsletter as Newsletter$1, UspGroup, FixedCta, BlogDetailHeader, BlogIntro } from '@grandvisionhq/content';
import { Heading as Heading$1, Article as Article$1, Container, Text } from '@grandvisionhq/elements';
import { ProductBlocksContainer, FeaturedProducts, Helpers, ProductCatalogBreadcrumbs, ProductPromotions, RecentlyViewedProducts as RecentlyViewedProducts$1 } from '@grandvisionhq/products';
import { Form, FirstName, MiddleName, LastName, Email, CheckboxList, Util, BookAppointment } from '@grandvisionhq/forms';
import { pushLoginAction, pushCheckedConsent, pushStartEyeTest, LaunchEyeExam } from '@grandvisionhq/eye-exam';
import { FlexLayout, Footer, FooterLinks, Header, SiteNav, getLocaleSwitcherMenuLabel, getLocaleSwitcherData, PageLayout } from '@grandvisionhq/layout';
import { setUserObject, WebPage, DataLayer } from '@grandvisionhq/analytics';
import { Actions, Selectors, State, getMyAccountMenuItems, MyAccountMenu } from '@grandvisionhq/auth';
import classnames from 'classnames';

/**
 * Creates an apiUrl that supports multilingual.
 * Don't pass locale for non-multilingual sites.
 * For multilingual it should be: [baseUrl]/en_GB/resourceapi/[path]
 * By default it should be: [baseUrl]/resourceapi/[path]
 */ function createBloomreachApiUrl(baseUrl, locale) {
    if (locale !== undefined && !baseUrl.includes(locale.replace('-', '_'))) {
        return `${baseUrl}/${locale.replace('-', '_')}`;
    }
    return baseUrl;
}

function getUrl(url, page) {
    // Check wether all settings are defined and correct and check if the link is relative
    // (absolute urls are usually external and don't need to be parsed by the bloomreach SDK)
    if (url && page && page.isPreview() && /^\/[a-zA-Z0-9_].*/.test(url)) {
        return page.getUrl(url);
    }
    return url;
}

function createMenu({ menu  } = {}, page) {
    if (!menu || !menu.siteMenuItems.length) {
        return [];
    }
    return mapToMenu(menu.siteMenuItems, page);
}
function mapToMenu(items, page) {
    return items.map((item)=>{
        const link = item._links.site;
        var ref;
        const menuItem = {
            label: item.name,
            link: swcHelpers.objectSpread({}, link, {
                href: (ref = parseHref(link, page)) !== null && ref !== void 0 ? ref : '',
                type: (link === null || link === void 0 ? void 0 : link.type) === 'internal' ? 'internal' : 'external'
            })
        };
        if (item.parameters && Object.keys(item.parameters).length > 0) {
            menuItem.parameters = item.parameters;
        }
        if (item.childMenuItems && item.childMenuItems.length > 0) {
            menuItem.childMenuItems = mapToMenu(item.childMenuItems, page);
        }
        return menuItem;
    });
}
function parseHref(link, page) {
    if (link && page) {
        return getUrl(link.href, page);
    }
}

/**
 * Locale needs to be omitted from the path for multilingual sites
 * For non-multilingual sites, make sure locale is not passed
 */ function createPath(path, locale) {
    // The Bloomreach `initialize` method returns a 404 error if a quote is sent in the path
    const cleanPath = path.replace(/'/g, '%27');
    if (locale === undefined) {
        return cleanPath;
    }
    const localePath = `/${locale.replace('-', '_')}`;
    // If the path is exactly equal to the localePath (e.g. home page request)
    // We need to return a / so we don't return an empty string
    if (cleanPath === localePath) {
        return '/';
    }
    return cleanPath.includes(localePath) ? cleanPath.replace(localePath, '') : cleanPath;
}

/**
 * Creates CMS resource url for Bloomreach
 * Locale should be formatted with an underscore like: "en_GB"
 */ function createBloomreachResourceUrl(baseUrl, channel, resourceIds, locale) {
    if (!baseUrl || !channel) {
        throw new Error(`Failed to create Bloomreach CMS resource url. Need valid baseUrl and channel but got baseUrl: ${String(baseUrl)} and channel: ${channel} }`);
    }
    const localePath = locale ? `/${locale.replace('-', '_')}` : '';
    const queryParam = `?resourceBundleIds=${encodeURIComponent(resourceIds.join(','))}`;
    /**
   * If the base URL contains a channel and the "channel" argument exactly
   * matches that, the channel will be moved from after the base URL to the
   * end of the URL, before the query params.
   */ const url = baseUrl.includes(channel) ? baseUrl.replace(`/${channel}`, '') : baseUrl;
    return url + '/api/resources' + localePath + `/${channel}` + queryParam;
}

/**
 * Converts default JSON structure of bloomreach resource API to flat format:
 * { [key: string (e.g. products)]: Object<key:value> }
 */ function flattenResources(resources) {
    var ref;
    const { bundles  } = (ref = resources === null || resources === void 0 ? void 0 : resources.resourceBundles) !== null && ref !== void 0 ? ref : {};
    const flatBundles = {};
    for(const key in bundles){
        if (bundles.hasOwnProperty(key)) {
            // get last part of key to create a shorter key in the new object
            const newKey = key.split('.')[key.split('.').length - 1];
            const { properties ={}  } = bundles[key];
            flatBundles[newKey] = properties;
        }
    }
    return flatBundles;
}

function getComponentByPath(path, pageModel) {
    const componentNames = path.split('/');
    if (!(pageModel === null || pageModel === void 0 ? void 0 : pageModel.page) || !componentNames) {
        return;
    }
    let component = pageModel.page;
    while(componentNames.length && component){
        var ref;
        const name = componentNames.shift();
        component = component === null || component === void 0 ? void 0 : (ref = component.components) === null || ref === void 0 ? void 0 : ref.find((c)=>c.name === name
        );
    }
    return component;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getContentByRef(ref, pageModel) {
    if (!ref || !pageModel) {
        return undefined;
    }
    return jsonpointer.get(pageModel, ref);
}

/**
 * This function is only used in opco sites to configure Algolia on the server.
 */ function getAlgoliaConfigFromBloomreach(pageModel) {
    var ref, ref1, ref2;
    if (!pageModel) {
        return {
            isEnabled: false
        };
    }
    const container = getComponentByPath('main/container', pageModel);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const component = container === null || container === void 0 ? void 0 : (ref = container.components) === null || ref === void 0 ? void 0 : ref.find((c)=>c.label === 'Product catalog v2'
    );
    const content = getContentByRef(component === null || component === void 0 ? void 0 : (ref1 = component.models) === null || ref1 === void 0 ? void 0 : (ref2 = ref1.document) === null || ref2 === void 0 ? void 0 : ref2.$ref, pageModel);
    const isEnabled = !!component;
    const presetQueryString = getPresetQueryString(content);
    var ref3;
    const ruleContexts = (ref3 = content === null || content === void 0 ? void 0 : content.ruleContexts) !== null && ref3 !== void 0 ? ref3 : [];
    const isPrescriptionGlasses = presetQueryString.toLowerCase().includes('frames') || presetQueryString.toLowerCase().includes('sunglasses');
    const facetingAfterDistinct = !isPrescriptionGlasses;
    return {
        isEnabled,
        presetQueryString,
        ruleContexts,
        facetingAfterDistinct
    };
}
function getPresetQueryString(content) {
    if (!content) {
        return '';
    }
    let presetQueryString = (content.filterQuery || '').split('#')[0];
    const categoryExternalId = content.categoryExternalId || '';
    if (categoryExternalId) {
        const symbol = presetQueryString.includes('?') ? '&' : '?';
        presetQueryString += symbol + `${FacetId.categoryExternalId}=${categoryExternalId}`;
    }
    return presetQueryString;
}

function getPageTemplate(pageModel, mapping) {
    var ref, ref1;
    if (!pageModel || !pageModel.page || !mapping) {
        return '/_error';
    }
    // PageId is more specific, so use that if that's available
    const id = (ref = pageModel.page.models) === null || ref === void 0 ? void 0 : ref.pageId;
    const type = (ref1 = pageModel.page.models) === null || ref1 === void 0 ? void 0 : ref1.pageType;
    if (!id && !type) {
        return '/_error';
    }
    if (id === 'pagenotfound' || type === 'pagenotfound') {
        return '/404';
    }
    return mapping[id] || mapping[type] || '/_error';
}

function getPrescriptionEntryList(configuratorDocumentRef, pageModel) {
    if (!pageModel) {
        return undefined;
    }
    var ref2;
    const { prescriptionStepEntryTypes  } = (ref2 = getContentByRef(configuratorDocumentRef === null || configuratorDocumentRef === void 0 ? void 0 : configuratorDocumentRef.$ref, pageModel)) !== null && ref2 !== void 0 ? ref2 : {};
    if (!prescriptionStepEntryTypes || prescriptionStepEntryTypes.length === 0) {
        return undefined;
    }
    return prescriptionStepEntryTypes.map((item)=>{
        var ref, ref1;
        return {
            key: item.key,
            title: item.iconTextCompound[0].text,
            icon: !!((ref = item.iconTextCompound[0]) === null || ref === void 0 ? void 0 : ref.icon) ? (ref1 = item.iconTextCompound[0]) === null || ref1 === void 0 ? void 0 : ref1.icon : undefined,
            description: !!item.description.value ? item.description.value : undefined,
            ctaLabel: !!item.ctaLabel ? item.ctaLabel : undefined
        };
    });
}

function getConfiguratorDocument(configuratorDocumentRef, pageModel, noAddonsImage) {
    if (!pageModel) {
        return undefined;
    }
    var ref;
    const { addOnStepReadMore , packageStepReadMore , prescriptionStepDescription , prescriptionStepImage , prescriptionFormStepDescription , prescriptionFormStepImage ,  } = (ref = getContentByRef(configuratorDocumentRef === null || configuratorDocumentRef === void 0 ? void 0 : configuratorDocumentRef.$ref, pageModel)) !== null && ref !== void 0 ? ref : {};
    const entryTypes = getPrescriptionEntryList(configuratorDocumentRef, pageModel);
    return {
        prescriptionStep: {
            entryTypes
        },
        prescriptionUploadStep: {
            description: prescriptionStepDescription === null || prescriptionStepDescription === void 0 ? void 0 : prescriptionStepDescription.value,
            image: prescriptionStepImage ? prescriptionStepImage : undefined
        },
        prescriptionFormStep: {
            description: prescriptionFormStepDescription === null || prescriptionFormStepDescription === void 0 ? void 0 : prescriptionFormStepDescription.value,
            image: prescriptionFormStepImage ? prescriptionFormStepImage : undefined
        },
        packageStep: {
            readMoreLink: !!(packageStepReadMore === null || packageStepReadMore === void 0 ? void 0 : packageStepReadMore.url) ? packageStepReadMore : undefined
        },
        addOnsStep: {
            readMoreLink: !!(addOnStepReadMore === null || addOnStepReadMore === void 0 ? void 0 : addOnStepReadMore.url) ? addOnStepReadMore : undefined,
            noAddonsImage
        }
    };
}

// There are 4 locations in the pageModel where a SEO config can be added
// For content pages it's always in the default "main/seo" path
const seoLocations = [
    'main/seo',
    'main/container/seo',
    'main/top-container/seo',
    'main/bottom-container/seo', 
];
function getPageSeo(pageModel) {
    if (!pageModel) {
        return undefined;
    }
    let seo;
    // Look for the first path we can find that contains a SEO component
    for (const path of seoLocations){
        const comp = getComponentByPath(path, pageModel);
        if (comp === null || comp === void 0 ? void 0 : comp.models) {
            seo = comp.models.seoData;
            break;
        }
    }
    return seo;
}

function getPlpPromotionSize(pageModel, breakpoint) {
    var ref, ref1, ref2, ref3;
    if (!pageModel) {
        return {};
    }
    const mainComponent = getComponentByPath('main', pageModel);
    const catalogComponent = (ref1 = mainComponent === null || mainComponent === void 0 ? void 0 : (ref = mainComponent.components) === null || ref === void 0 ? void 0 : ref[0].components) === null || ref1 === void 0 ? void 0 : ref1.find(// eslint-disable-next-line @typescript-eslint/no-explicit-any
    (c)=>c.label === 'Product catalog v2'
    );
    if (!catalogComponent) {
        return {};
    }
    const ref4 = (ref2 = catalogComponent.models) === null || ref2 === void 0 ? void 0 : (ref3 = ref2.document) === null || ref3 === void 0 ? void 0 : ref3.$ref;
    const content = getContentByRef(ref4, pageModel);
    const { smallPromotion , bigPromotion  } = content !== null && content !== void 0 ? content : {};
    const isSmallScreen = breakpoint === MediaQuery.BreakpointName.PHONE;
    const smallPromoSize = smallPromotion ? isSmallScreen ? 2 : 1 : 0;
    const bigPromoSize = bigPromotion ? 2 : 0;
    return {
        plpPromotionSize: smallPromoSize + bigPromoSize
    };
}

function parseBloomreachImage(image) {
    var ref, ref1;
    if (!image) {
        return undefined;
    }
    var _description, _description1, _description2;
    return {
        alt: (_description = image.description) !== null && _description !== void 0 ? _description : '',
        original: {
            alt: (_description1 = image.description) !== null && _description1 !== void 0 ? _description1 : '',
            url: ((ref = image.original) === null || ref === void 0 ? void 0 : ref.url) || '',
            dimensions: getDimensions(image.original)
        },
        mobile: {
            alt: (_description2 = image.description) !== null && _description2 !== void 0 ? _description2 : '',
            url: ((ref1 = image.mobile) === null || ref1 === void 0 ? void 0 : ref1.url) || '',
            dimensions: getDimensions(image.mobile)
        }
    };
}
function getDimensions(input) {
    return {
        w: (input === null || input === void 0 ? void 0 : input.width) || 0,
        h: (input === null || input === void 0 ? void 0 : input.height) || 0
    };
}

function parseBloomreachInputField(inputField) {
    return inputField ? {
        placeholder: inputField.placeholder,
        id: inputField.inputId,
        type: inputField.type || 'text'
    } : undefined;
}

const nonLetterGroupTitle = '#';
// Replace with /^p{L}/u once IE11 support is dropped
const startsWithLetter = /^[a-zA-ZÀÈÌÒÙàèìòùÁÉÍÓÚÝáéíóúýÂÊÎÔÛâêîôûÃÑÕãñõÄËÏÖÜŸäëïöüŸçÇŒœßØøÅåÆæÞþÐð]/i;
const groupTitle = (title)=>{
    return startsWithLetter.test(title) ? title === null || title === void 0 ? void 0 : title.charAt(0).toUpperCase() : nonLetterGroupTitle;
};
const groupBrands = (items, maxGroupLength = 4, locale)=>{
    if (!items) {
        return [];
    }
    let currentTitle;
    return items.sort((a, b)=>{
        const aLetter = startsWithLetter.test(a.title);
        const bLetter = startsWithLetter.test(b.title);
        return aLetter && !bLetter ? 1 : !aLetter && bLetter ? -1 : a.title.localeCompare(b.title, locale, {
            sensitivity: 'base',
            usage: 'sort',
            numeric: true
        });
    }).reduce((result, item)=>{
        const title = groupTitle(item.title);
        let currentGroup = result[result.length - 1];
        if (title !== currentTitle) {
            currentTitle = title;
            currentGroup = {
                title,
                items: []
            };
            result.push(currentGroup);
        }
        if (currentGroup.items.length === maxGroupLength) {
            currentGroup = {
                title: '',
                items: []
            };
            result.push(currentGroup);
        }
        currentGroup.items.push(item);
        return result;
    }, []);
};

/**
 * We need this function to make sure we can set the BrPageModel type
 * because the toJSON function from the SDK has return type of "object".
 * This function will no longer be required when this is fixed in the SDK.
 */ function parsePageModel(page) {
    const pageModel = (page === null || page === void 0 ? void 0 : page.toJSON()) || {};
    return pageModel;
}

const fetch$2 = resilientFetch();
function brHttpClient(options) {
    return _brHttpClient.apply(this, arguments);
}
function _brHttpClient() {
    _brHttpClient = swcHelpers.asyncToGenerator(function*(options) {
        const { url , data  } = options, rest = swcHelpers.objectWithoutProperties(options, [
            "url",
            "data"
        ]);
        const response = yield fetch$2(url, swcHelpers.objectSpread({}, rest, {
            body: data
        }));
        // Some of the error responses actually contain html instead of json
        // This makes sure we don't actually try to parse html as json
        const contentType = response === null || response === void 0 ? void 0 : response.headers.get('content-type');
        if (!(contentType === null || contentType === void 0 ? void 0 : contentType.includes('application/json'))) {
            throw new Error(`Expected Bloomreach response type to be 'application/json' but got '${contentType}' instead`);
        }
        const json = yield response.json();
        return {
            data: json
        };
    });
    return _brHttpClient.apply(this, arguments);
}

/**
 * Retrieves the brandCollection from the pagemodel
 * Iterates over the individual brands and parses the data.
 * Returns a parsed BrandPromotion array
 */ function getBrandPromotions(brandCollectionRef, pageModel) {
    if (!brandCollectionRef) {
        return [];
    }
    // The brands are sent as refs as well, so we need to fetch these refs
    const brandCollection = getContentByRef(brandCollectionRef, pageModel);
    const { brands  } = brandCollection || {};
    if (!brands || brands.length === 0) {
        return [];
    }
    // Parse individual brand information, filter out nullish entries
    const parsedBrands = brands.map(parseBrandFromCollection).filter((brand)=>Boolean(brand)
    );
    return parsedBrands;
}
/**
 * Get the brand content at the provided $ref from the pageModel
 * returns CmsBrandPromotion data or null if data is missing.
 */ function parseBrandFromCollection(brand) {
    // const brand = getContentByRef<BrandDocument>(brandRef, pageModel)
    const { promotionLink , promotionLogo , title , clickablePromotionLogo  } = brand !== null && brand !== void 0 ? brand : {};
    // Return null if we don't have the correct data
    if (!title) {
        return null;
    }
    return {
        name: title,
        url: promotionLink === null || promotionLink === void 0 ? void 0 : promotionLink.url,
        logo: promotionLogo === null || promotionLogo === void 0 ? void 0 : promotionLogo.url,
        clickableLogo: clickablePromotionLogo === null || clickablePromotionLogo === void 0 ? void 0 : clickablePromotionLogo.url
    };
}

const getProductCategories = (productCategoriesCompound, pageModel)=>{
    return productCategoriesCompound === null || productCategoriesCompound === void 0 ? void 0 : productCategoriesCompound.map(({ categoryTitle , description , imageLink , link , key  })=>{
        var ref, ref1, ref2;
        const imageRef = (imageLink === null || imageLink === void 0 ? void 0 : imageLink.$ref) || undefined;
        const imageObject = getContentByRef(imageRef, pageModel);
        return {
            title: categoryTitle,
            description,
            image: {
                url: (imageObject === null || imageObject === void 0 ? void 0 : (ref = imageObject.original) === null || ref === void 0 ? void 0 : (ref1 = ref._links) === null || ref1 === void 0 ? void 0 : (ref2 = ref1.site) === null || ref2 === void 0 ? void 0 : ref2.href) || '',
                description: (imageObject === null || imageObject === void 0 ? void 0 : imageObject.description) || null
            },
            key,
            link: link === null || link === void 0 ? void 0 : link.url
        };
    });
};

function getVtoBanner(pageModel, ref) {
    const brVtoBanner = getContentByRef(ref, pageModel);
    if (!brVtoBanner) {
        return undefined;
    }
    return {
        title: brVtoBanner.title,
        description: brVtoBanner.description,
        image: brVtoBanner.image
    };
}

const fetch$1 = resilientFetch();
function getLabels(apiUrl, channel, resourceIds, locale) {
    return _getLabels.apply(this, arguments);
}
function _getLabels() {
    _getLabels = swcHelpers.asyncToGenerator(function*(apiUrl, channel, resourceIds, locale) {
        const url = createBloomreachResourceUrl(apiUrl, channel, resourceIds, locale.replace('-', '_'));
        try {
            const response = yield fetch$1(url, {
                method: 'GET',
                headers: {
                    Accept: 'application/json'
                }
            });
            if (!response.ok) {
                throw new Error(`received ${response.status} from Bloomreach`);
            }
            const resources = yield response.json();
            const labelSets = flattenResources(resources);
            return mergeLabels(labelSets);
        } catch (err) {
            console.error(`Error while fetching labels for ${url}`, err);
        }
    });
    return _getLabels.apply(this, arguments);
}

function getPageModel(apiUrl, path, opts) {
    return _getPageModel.apply(this, arguments);
}
function _getPageModel() {
    _getPageModel = swcHelpers.asyncToGenerator(function*(apiUrl, path, opts) {
        const { headers ={} , connection , visitor , locale , isMultilingual  } = opts;
        if (!apiUrl) {
            throw new Error(`Need a valid Bloomreach api url to fetch a pageModel! Got apiUrl: ${apiUrl}`);
        }
        if (!path) {
            throw new Error(`Need a valid path to fetch a pageModel! Got path: ${path}`);
        }
        if (isMultilingual && !locale) {
            throw new Error(`Need a valid locale when isMultilingual is set to true! Got locale: ${locale}`);
        }
        const cmsBaseUrl = createBloomreachApiUrl(apiUrl, isMultilingual ? locale : undefined);
        const parsedPath = createPath(path, isMultilingual ? locale : undefined);
        const request = {
            connection,
            path: parsedPath,
            headers: swcHelpers.objectSpread({}, headers['x-forwarded-for'] ? {
                'x-forwarded-for': headers['x-forwarded-for']
            } : {})
        };
        const { origin  } = new URL(apiUrl);
        const configuration = {
            cmsBaseUrl,
            visitor,
            request,
            httpClient: brHttpClient,
            origin: origin.replace('www', 'cms')
        };
        if (process.env.NODE_ENV !== 'production') {
            console.log(`Fetching: ${cmsBaseUrl}/resourceapi${parsedPath}`);
        }
        return initialize(configuration);
    });
    return _getPageModel.apply(this, arguments);
}

var index = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getLabels: getLabels,
  getPageModel: getPageModel
});

function withAnchor(Component) {
    const displayName = getDisplayName(Component);
    const WithAnchor = (props)=>{
        const { page , component  } = props;
        const preview = page.isPreview();
        var ref;
        const { anchorId  } = (ref = component === null || component === void 0 ? void 0 : component.getParameters()) !== null && ref !== void 0 ? ref : {};
        return anchorId && anchorId !== '' && !preview ? /*#__PURE__*/ React.createElement("div", {
            className: "anchor-wrapper",
            id: anchorId,
            style: {
                marginTop: '-120px',
                paddingTop: '120px'
            }
        }, /*#__PURE__*/ React.createElement(Component, swcHelpers.extends({}, props))) : /*#__PURE__*/ React.createElement(Component, swcHelpers.extends({}, props));
    };
    WithAnchor.displayName = `withAnchor(${displayName})`;
    return WithAnchor;
}

const initialValue = Object.freeze({
    apiUrl: '',
    componentDefinitions: {},
    preview: false,
    pageModel: undefined,
    path: '',
    excludeSkuFromProductUrls: false,
    siteLogos: {
        main: '',
        small: '',
        mobile: '',
        alternative: '',
        reimbursable: '',
        reimbursableAlternative: '',
        payInstallmentsLogo: '',
        logoFooter: ''
    }
});
const VISITOR_COOKIE = '_visitor';
/**
 * Note: do not use BloomreachContext.Provider directly. Instead, import {
 * BloomreachProvider } from './BloomreachProvider'. It's in a separate module
 * to avoid a circular dependency between ../component-definitions and
 * ../containers.
 */ const BloomreachContext = /*#__PURE__*/ React.createContext(initialValue);
function useBloomreach() {
    return React.useContext(BloomreachContext);
}
/**
 * Wrap Contexts from BR SDK in a hook so they're easier to mock in tests
 */ function useBrPageContext() {
    return React.useContext(BrPageContext);
}
function useBrComponentContext() {
    return React.useContext(BrComponentContext);
}

/**
 * @deprecated - use useBloomreachAppointmentContent
 */ const useBloomreachAppointmentTypes = ()=>{
    var ref, ref1, ref2;
    const { pageModel  } = useBloomreach();
    if (!(pageModel === null || pageModel === void 0 ? void 0 : (ref = pageModel.page) === null || ref === void 0 ? void 0 : ref.models)) {
        return {
            appointmentTypes: []
        };
    }
    const mainComponent = getComponentByPath('main', pageModel);
    const ref3 = mainComponent === null || mainComponent === void 0 ? void 0 : (ref1 = mainComponent.models) === null || ref1 === void 0 ? void 0 : (ref2 = ref1.appointmentConfigurationDocument) === null || ref2 === void 0 ? void 0 : ref2.$ref;
    const content = getContentByRef(ref3, pageModel);
    if (!content) {
        console.warn(`Cannot find main content for BookAppointment pageModel. Got: ${content} for $ref: ${ref3}`);
        return {
            appointmentTypes: []
        };
    }
    return content;
};

const fromAppointmentBannerToCampaignInfoBanner = (appointmentBanner)=>{
    let startEndDateCompound;
    if (appointmentBanner === null || appointmentBanner === void 0 ? void 0 : appointmentBanner.startEndDateCompound) {
        /* appointmentBanner.startEndDateCompound contains
      more properties that we dont care about */ const { startDate , endDate  } = appointmentBanner.startEndDateCompound;
        startEndDateCompound = {
            startDate,
            endDate
        };
    }
    return {
        startEndDateCompound,
        bannerIcon: appointmentBanner === null || appointmentBanner === void 0 ? void 0 : appointmentBanner.bannerIcon,
        bannerTitle: appointmentBanner === null || appointmentBanner === void 0 ? void 0 : appointmentBanner.bannerTitle
    };
};
const fromSpotlightedStoresContentToListBlock = (idPrefix, column)=>{
    if (!column || !column.storeBlock) {
        return {
            title: '',
            links: [],
            id: `${idPrefix}-column`
        };
    }
    const { storeBlock  } = column;
    var _store;
    const stores = (_store = storeBlock.store) !== null && _store !== void 0 ? _store : [];
    var ref;
    return {
        title: (ref = storeBlock === null || storeBlock === void 0 ? void 0 : storeBlock.title) !== null && ref !== void 0 ? ref : '',
        id: `${idPrefix}-column`,
        links: stores.filter((item)=>!!item.label
        ).map(({ url , openInNewWindow , label  }, index)=>({
                label,
                url: url !== null && url !== void 0 ? url : undefined,
                openInNewWindow,
                id: `${idPrefix}-${index}-item`
            })
        )
    };
};

const useBloomreachAppointmentContent = ()=>{
    var ref, ref1, ref2, ref3, ref4, ref5;
    const { pageModel  } = useBloomreach();
    if (!(pageModel === null || pageModel === void 0 ? void 0 : (ref = pageModel.page) === null || ref === void 0 ? void 0 : ref.models)) {
        return {
            appointmentTypes: [],
            campaignInfoBanner: null,
            appointmentFaq: null
        };
    }
    const mainComponent = getComponentByPath('main', pageModel);
    const ref6 = mainComponent === null || mainComponent === void 0 ? void 0 : (ref1 = mainComponent.models) === null || ref1 === void 0 ? void 0 : (ref2 = ref1.appointmentConfigurationDocument) === null || ref2 === void 0 ? void 0 : ref2.$ref;
    const content = getContentByRef(ref6, pageModel);
    if (!content) {
        console.warn(`Cannot find main content for BookAppointment pageModel. Got: ${content} for $ref: ${ref6}`);
        return {
            appointmentTypes: [],
            campaignInfoBanner: null,
            appointmentFaq: null
        };
    }
    return {
        appointmentTypes: content.appointmentTypes,
        campaignInfoBanner: fromAppointmentBannerToCampaignInfoBanner(content.appointmentBanner),
        // Because of the structure of the returned Bloomreach data we need to restructure it,
        // from objects that contain $ref to ones that contain an id, a question and an answer.
        appointmentFaq: {
            title: (ref3 = content.faqListAppointment) === null || ref3 === void 0 ? void 0 : ref3.title,
            faqItems: (ref4 = content.faqListAppointment) === null || ref4 === void 0 ? void 0 : (ref5 = ref4.faqitem) === null || ref5 === void 0 ? void 0 : ref5.map(({ $ref  })=>getContentByRef($ref, pageModel)
            ).filter(({ type  })=>type === 'faqitem'
            ).map(({ id , question , answer  })=>({
                    id,
                    question,
                    answer: answer.value
                })
            )
        }
    };
};

const parselinkConfigurator = (linkConfigurator, page)=>{
    var _label, ref, ref1;
    return {
        label: (_label = linkConfigurator === null || linkConfigurator === void 0 ? void 0 : linkConfigurator.linkSelector.label) !== null && _label !== void 0 ? _label : '',
        url: (ref = getUrl(linkConfigurator === null || linkConfigurator === void 0 ? void 0 : linkConfigurator.linkSelector.url, page)) !== null && ref !== void 0 ? ref : '',
        icon: (ref1 = linkConfigurator === null || linkConfigurator === void 0 ? void 0 : linkConfigurator.icon) !== null && ref1 !== void 0 ? ref1 : ''
    };
};
// This is the key that BR uses to set the hidden property on a container component
const PARAMETER_HIDDEN = 'com.onehippo.cms7.targeting.TargetingParameterUtil.hide';
// Default properties on bloomreach content and meta objects
// that are always there, but we don't require on component level
const omitKeysContent = [
    'displayName',
    'versionedNode',
    '_links',
    'localeString',
    'type'
];
const omitKeysMeta = [
    PARAMETER_HIDDEN,
    'document',
    'mobile',
    'tablet',
    'desktop'
];
function parseBloomreachContent(content = {}) {
    return Object.keys(content).reduce((result, key)=>omitKeysContent.includes(key) ? result : swcHelpers.objectSpread({}, result, {
            [key]: content[key]
        })
    , {});
}
function parseBloomreachMeta(meta) {
    return Object.keys(meta).reduce((result, key)=>omitKeysMeta.includes(key) ? result : swcHelpers.objectSpread({}, result, {
            [key]: meta[key]
        })
    , {});
}
const getCommonParams = ({ title , theme , template  })=>swcHelpers.objectSpread({}, isString(title) ? {
        title
    } : {}, isString(theme) ? {
        theme
    } : {}, isString(template) ? {
        template
    } : {})
;
function fetchPagedComponent(url, componentId) {
    return _fetchPagedComponent.apply(this, arguments);
}
function _fetchPagedComponent() {
    _fetchPagedComponent = swcHelpers.asyncToGenerator(function*(url, componentId, pageIndex = 1, path = '/blog') {
        const response = yield fetch(`${url}/resourceapi${path}?_hn%3Atype=component-rendering&_hn%3Aref=${componentId}&page=${pageIndex}`, {
            method: 'GET'
        });
        return response.json();
    });
    return _fetchPagedComponent.apply(this, arguments);
}

function parseBloomreachArticles(pageable, highlighted, pageModel) {
    const { currentPage , nextPage , totalPages , items  } = pageable !== null && pageable !== void 0 ? pageable : {};
    const highlightedContent = highlighted && getContentByRef(highlighted.$ref, pageModel);
    var ref;
    const articles = (ref = items === null || items === void 0 ? void 0 : items.map((item)=>item && getContentByRef(item.$ref, pageModel)
    )) !== null && ref !== void 0 ? ref : [];
    return {
        highlighted: highlightedContent,
        paged: {
            items: articles,
            currentPage,
            nextPage,
            totalPages
        }
    };
}
const useBloomreachArticles = ({ component , page  })=>{
    const { apiUrl  } = useBloomreach();
    const { locale  } = useIntl();
    const { isMultilingualEnabled  } = useFeatureToggles();
    const componentId = component.getId();
    const pageModel = page.toJSON();
    const localeTransformed = locale.replace('-', '_');
    const url = isMultilingualEnabled ? `${apiUrl}/${localeTransformed}` : apiUrl;
    const { pageable: pageable1 , highlightedArticle: highlightedArticle1  } = component.getModels();
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState();
    const [result, setResult] = React.useState(()=>{
        return parseBloomreachArticles(pageable1, highlightedArticle1, pageModel);
    });
    var _currentPage;
    const [currentPage, setCurrentPage] = React.useState((_currentPage = result.paged.currentPage) !== null && _currentPage !== void 0 ? _currentPage : 1);
    const loadNextPage = React.useCallback(()=>{
        result.paged.nextPage && setCurrentPage(result.paged.nextPage);
    }, [
        result.paged.nextPage
    ]);
    React.useEffect(()=>{
        if (currentPage === result.paged.currentPage) {
            return;
        }
        const path = isMultilingualEnabled ? window === null || window === void 0 ? void 0 : window.location.pathname.replace(`/${localeTransformed}`, '') : window === null || window === void 0 ? void 0 : window.location.pathname;
        let current = true;
        setLoading(true);
        fetchPagedComponent(url, componentId, currentPage, path).then((res)=>{
            if (current) {
                var ref;
                if (res === null || res === void 0 ? void 0 : (ref = res.page) === null || ref === void 0 ? void 0 : ref.models) {
                    const { pageable , highlightedArticle  } = res.page.models;
                    const { paged  } = parseBloomreachArticles(pageable, highlightedArticle, res);
                    setResult((state)=>swcHelpers.objectSpread({}, state, {
                            paged: swcHelpers.objectSpread({}, paged, {
                                items: [
                                    ...state.paged.items,
                                    ...paged.items
                                ]
                            })
                        })
                    );
                }
                setLoading(false);
            }
        }).catch((err)=>{
            if (current) {
                console.error(err);
                setError(err);
                setLoading(false);
            }
        });
        return ()=>{
            current = false;
        };
    }, [
        url,
        currentPage,
        result.paged.currentPage,
        page,
        componentId,
        isMultilingualEnabled,
        localeTransformed, 
    ]);
    return {
        loading,
        error,
        result,
        loadNextPage
    };
};

/**
 * Receives a configuration for a Bloomreach container component
 * and maps it to our own CmsComponentData model
 */ // eslint-disable-next-line @typescript-eslint/no-explicit-any
function useBloomreachData({ page , component  }) {
    const { document: documentRef  } = component.getModels();
    const document = documentRef && page.getContent(documentRef);
    const content = swcHelpers.objectSpread({}, page.isPreview() ? {
        document
    } : {}, parseBloomreachContent(document === null || document === void 0 ? void 0 : document.getData()));
    const meta = parseBloomreachMeta(component.getParameters());
    const common = getCommonParams(meta);
    return {
        ref: documentRef === null || documentRef === void 0 ? void 0 : documentRef.$ref,
        data: swcHelpers.objectSpread({
            meta,
            content
        }, common)
    };
}

function useBloomreachLinks(links = []) {
    const page = useBrPageContext();
    var ref;
    return links.map(({ url , label , style , openInNewWindow  })=>({
            label,
            style,
            url: (ref = getUrl(url, page)) !== null && ref !== void 0 ? ref : '',
            target: openInNewWindow ? 'external' : 'internal'
        })
    );
}

const useBloomreachDeliveryInfo = ()=>{
    var ref, ref1, ref2;
    const { pageModel  } = useBloomreach();
    const ref3 = (ref = pageModel === null || pageModel === void 0 ? void 0 : pageModel.page.models) === null || ref === void 0 ? void 0 : (ref1 = ref.deliveryInfo) === null || ref1 === void 0 ? void 0 : ref1.$ref;
    const content = getContentByRef(ref3, pageModel);
    const { deliveryItems =[] , description: description1 , link: brLink , linkHint , title: title1  } = content !== null && content !== void 0 ? content : {};
    const [link] = brLink ? useBloomreachLinks([
        brLink
    ]) : [];
    if (!(pageModel === null || pageModel === void 0 ? void 0 : (ref2 = pageModel.page) === null || ref2 === void 0 ? void 0 : ref2.models) || !content) {
        console.warn(`Cannot find main content for Delivery Info. Got: ${content} for $ref: ${ref3}`);
        return;
    }
    const items = deliveryItems.filter(({ title  })=>!!title
    ).map(({ description , id , image , price , title  })=>({
            description: description.value,
            id,
            image: parseBloomreachImage(image),
            price,
            title
        })
    );
    return {
        items,
        description: (description1 === null || description1 === void 0 ? void 0 : description1.value) || '',
        link,
        linkHint: linkHint || '',
        title: title1 || ''
    };
};

function useBloomreachPageRoutes() {
    var ref, ref1;
    const { pageModel , preview  } = useBloomreach();
    if (!(pageModel === null || pageModel === void 0 ? void 0 : (ref = pageModel.page) === null || ref === void 0 ? void 0 : (ref1 = ref.models) === null || ref1 === void 0 ? void 0 : ref1.routes)) {
        return {
            routes: {}
        };
    }
    const { routes  } = pageModel.page.models;
    // In the new version of bloomreach in preview mode the params that
    // are added by the bloomreach API can cause issues for the root routes sometimes
    // because these are base routes that are usually joined with other paths
    let cleanedRoutes = routes;
    if (preview) {
        cleanedRoutes = Object.keys(routes).reduce((acc, key)=>{
            const route = routes[key].split('?')[0];
            return swcHelpers.objectSpread({}, acc, {
                [key]: route !== null && route !== void 0 ? route : ''
            });
        }, {});
    }
    return {
        routes: cleanedRoutes
    };
}

function useBloomreachPageable({ page , component  }) {
    const { pageable  } = component.getModels();
    if (!pageable || !pageable.items) {
        return {
            refs: pageable === null || pageable === void 0 ? void 0 : pageable.items,
            data: {
                meta: {},
                content: {
                    items: []
                }
            }
        };
    }
    const documents = pageable.items.map((ref)=>page.getContent(ref)
    );
    const items = documents.map((doc)=>{
        return swcHelpers.objectSpread({}, page.isPreview() ? {
            document: doc
        } : {}, parseBloomreachContent(doc === null || doc === void 0 ? void 0 : doc.getData()));
    });
    const meta = parseBloomreachMeta(component.getParameters());
    const commonParams = getCommonParams(meta);
    return {
        refs: pageable.items,
        data: swcHelpers.objectSpread({
            meta,
            content: {
                items
            }
        }, commonParams)
    };
}

const emptyContent = {};
function useBloomreachPdpContent() {
    var ref, ref1, ref2, ref3, ref4;
    const { pageModel  } = useBloomreach();
    if (!(pageModel === null || pageModel === void 0 ? void 0 : (ref = pageModel.page) === null || ref === void 0 ? void 0 : ref.models)) {
        return emptyContent;
    }
    const mainComponent = getComponentByPath('main', pageModel);
    const ref5 = mainComponent === null || mainComponent === void 0 ? void 0 : (ref1 = mainComponent.models) === null || ref1 === void 0 ? void 0 : ref1.document.$ref;
    const content = getContentByRef(ref5, pageModel);
    if (!content) {
        console.warn(`Cannot find main content for ProductDetail pageModel. Got: ${content} for $ref: ${ref5}`);
        return emptyContent;
    }
    const { usps , instructions , noAddonsImage , additionalUsps , disclaimer , configuratorDocument: configuratorDocumentRef , brandCollectionDocument ,  } = content;
    const configuratorDocument = getConfiguratorDocument(configuratorDocumentRef, pageModel, noAddonsImage);
    const modal = getModalContent(mainComponent, pageModel);
    const sizeGuideDocumentRef = content === null || content === void 0 ? void 0 : (ref2 = content.sizeGuideDocument) === null || ref2 === void 0 ? void 0 : ref2.$ref;
    const sizeGuideDocument = getContentByRef(sizeGuideDocumentRef, pageModel);
    const vtoBanner = getVtoBanner(pageModel, (ref3 = content.vtoBanner) === null || ref3 === void 0 ? void 0 : ref3.$ref);
    const brandPromotions = getBrandPromotions(brandCollectionDocument === null || brandCollectionDocument === void 0 ? void 0 : brandCollectionDocument.$ref, pageModel);
    var ref6;
    return {
        modalContent: (ref6 = modal === null || modal === void 0 ? void 0 : (ref4 = modal.content) === null || ref4 === void 0 ? void 0 : ref4.value) !== null && ref6 !== void 0 ? ref6 : '',
        instructions: instructions && {
            id: instructions.id,
            value: instructions.html,
            textColor: instructions.textColor,
            backgroundColor: instructions.backgroundColor
        },
        usps: usps.map(({ icon , text  })=>({
                icon,
                text
            })
        ),
        additionalUsps: additionalUsps.map(({ icon , text  })=>({
                icon,
                text
            })
        ),
        configuratorDocument,
        brandPromotions,
        vtoBanner,
        disclaimer,
        sizeGuideDocument: sizeGuideDocument && {
            findOutSizeCTA: sizeGuideDocument.findOutSizeCTA && {
                icon: sizeGuideDocument.findOutSizeCTA.icon,
                link: {
                    url: sizeGuideDocument.findOutSizeCTA.link.url,
                    label: sizeGuideDocument.findOutSizeCTA.link.label,
                    alt: sizeGuideDocument.findOutSizeCTA.link.alt,
                    openInNewWindow: sizeGuideDocument.findOutSizeCTA.link.openInNewWindow,
                    style: sizeGuideDocument.findOutSizeCTA.link.style
                }
            },
            findOutSizeDescription: sizeGuideDocument.findOutSizeDescription,
            findOutSizeImage: sizeGuideDocument.findOutSizeImage,
            findOutSizeSubtitle: sizeGuideDocument.findOutSizeSubtitle,
            findOutSizeTitle: sizeGuideDocument.findOutSizeTitle,
            measureDescription: sizeGuideDocument.measureDescription,
            measureDescriptionImage: sizeGuideDocument.measureDescriptionImage,
            measureImage: sizeGuideDocument.measureImage,
            measureSteps: sizeGuideDocument.measureSteps,
            measureTitle: sizeGuideDocument.measureTitle,
            sizeGuideSizeCompound: sizeGuideDocument.sizeGuideSizeCompound,
            sizeTitle: sizeGuideDocument.sizeTitle
        }
    };
}
function getModalContent(main, pageModel) {
    var ref9, ref7;
    const components = main === null || main === void 0 ? void 0 : (ref9 = main.components) === null || ref9 === void 0 ? void 0 : ref9.reduce((acc, cur)=>{
        return (cur === null || cur === void 0 ? void 0 : cur.components) ? [
            ...acc,
            ...cur === null || cur === void 0 ? void 0 : cur.components
        ] : acc;
    }, []);
    const modal = (components || []).find((c)=>{
        var ref, ref8;
        return c.name === 'richtext' && ((ref = c._meta) === null || ref === void 0 ? void 0 : (ref8 = ref.params) === null || ref8 === void 0 ? void 0 : ref8.template) === 'modal';
    });
    return modal && getContentByRef(modal === null || modal === void 0 ? void 0 : (ref7 = modal.models) === null || ref7 === void 0 ? void 0 : ref7.document.$ref, pageModel);
}

function useBloomreachProductRoutes() {
    var ref, ref1;
    const { locale  } = useIntl();
    const { pageModel , excludeSkuFromProductUrls  } = useBloomreach();
    const page = useBrPageContext();
    var ref2;
    const productRoutes = (ref2 = pageModel === null || pageModel === void 0 ? void 0 : (ref = pageModel.page) === null || ref === void 0 ? void 0 : (ref1 = ref.models) === null || ref1 === void 0 ? void 0 : ref1.productRoutes) !== null && ref2 !== void 0 ? ref2 : {};
    const hasRoutes = productRoutes && Object.keys(productRoutes).length > 0;
    if (!hasRoutes) {
        var ref3;
        console.log(`Could not get productRoutes for page with name ${pageModel === null || pageModel === void 0 ? void 0 : (ref3 = pageModel.page) === null || ref3 === void 0 ? void 0 : ref3.name}, got: ${String(productRoutes)}`);
    }
    const sortedProductRoutes = hasRoutes ? Object.entries(productRoutes).reduce((acc, [productType, route], i)=>{
        // TODO: remove when BR pageModel is updated
        // Skip legacy implementation key
        if (productType === '$ref') {
            return acc;
        }
        acc.byProductType[productType] = route;
        acc.checksum = `${acc.checksum}|${i}-${productType}:${route}`;
        return acc;
    }, {
        byProductType: {},
        checksum: ''
    }) : null;
    const formatProductUrl = React.useCallback((product, additionalQueryParams = {})=>{
        if (!CommerceToolsApi.isProductTypeName(product.productType) || !sortedProductRoutes) {
            return '#';
        }
        const targetPath = sortedProductRoutes.byProductType[product.productType];
        if (!targetPath) {
            console.warn(`No targetPath (root of url) found to format product url for productType ${product.productType}, got targetPath: ${String(targetPath)}`);
            return '';
        }
        const [path, query] = targetPath.split('?');
        const slug = typeof product.slug === 'string' ? product.slug : getLocalizedValue(product.slug, locale);
        const sku = !!product.sku && !excludeSkuFromProductUrls ? `/${product.sku}` : '';
        // Merge original query from url with any additionalQueryParams that were passed
        const allQueryParams = swcHelpers.objectSpread({}, Boolean(query) ? QueryString.parse(query) : {}, additionalQueryParams);
        const queryString = Object.keys(allQueryParams).length > 0 ? QueryString.stringify(allQueryParams) : '';
        const productUrl = `${path}/${slug}${sku}${queryString}`;
        return getUrl(productUrl, page) || '';
    }, [
        sortedProductRoutes,
        locale,
        excludeSkuFromProductUrls,
        page
    ]);
    // Omit a $ref from the productRoutes object
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const routes = swcHelpers.objectWithoutProperties(productRoutes, [
        "$ref"
    ]);
    return React.useMemo(()=>[
            {
                hasRoutes
            },
            {
                formatProductUrl
            },
            routes
        ]
    , [
        hasRoutes,
        formatProductUrl,
        routes, 
    ]);
}

function useBloomreachRichText(html) {
    const page = useBrPageContext();
    if (html) {
        return html.replace(/<a href="([^"]*)"/g, (_, $1)=>{
            return `<a href="${getUrl($1, page)}"`;
        });
    }
    return '';
}

/**
 * List of breakpoint names supported by Bloomreach. By using this list, components will always be visible
 * in any new breakpoint by default (no option to hide them until Bloomreach supports the new breakpoint)
 * If a breakpoint ever gets removed/replaced in the original enum, Bloomreach needs to be updated first to support the change
 */ const supportedBreakpoints = [
    MediaQuery.BreakpointName.PHONE,
    MediaQuery.BreakpointName.TABLET_PORTRAIT,
    MediaQuery.BreakpointName.TABLET_LANDSCAPE,
    MediaQuery.BreakpointName.DESKTOP, 
];
const isComponentVisible = (breakpoint, visibility)=>!visibility || !supportedBreakpoints.includes(breakpoint) || visibility.includes(breakpoint)
;
function useBreakpointVisibility({ component , page  }) {
    const breakpoint = MediaQuery.useBreakpoint();
    const preview = page.isPreview();
    const { visibility  } = component === null || component === void 0 ? void 0 : component.getModels();
    return preview || isComponentVisible(breakpoint, visibility);
}

const useBloomreachSdpContent = ()=>{
    var ref, ref1, ref2, ref3, ref4, ref5, ref6, ref7, ref8, ref9;
    const { pageModel  } = useBloomreach();
    if (!(pageModel === null || pageModel === void 0 ? void 0 : (ref = pageModel.page) === null || ref === void 0 ? void 0 : ref.models)) {
        return {};
    }
    const mainComponent = getComponentByPath('main', pageModel);
    const ref10 = mainComponent === null || mainComponent === void 0 ? void 0 : (ref1 = mainComponent.models) === null || ref1 === void 0 ? void 0 : (ref2 = ref1.storeDetailDocument) === null || ref2 === void 0 ? void 0 : ref2.$ref;
    const content = getContentByRef(ref10, pageModel);
    // There is a reference to a image because of the getLinkedBean function in bloomreach
    // A nicer solution would be that we get just a value with the singleProperty() java function.
    const storeDetailCampaignRef = (content === null || content === void 0 ? void 0 : (ref3 = content.storeDetailCampaign) === null || ref3 === void 0 ? void 0 : (ref4 = ref3.image) === null || ref4 === void 0 ? void 0 : ref4.$ref) || undefined;
    const campaignBannerContent = getContentByRef(storeDetailCampaignRef, pageModel);
    const productCategories = getProductCategories(content === null || content === void 0 ? void 0 : content.productCategoriesCompound, pageModel);
    if (!content) {
        console.warn(`Cannot find main content for StoreDetailPage pageModel. Got: ${content} for $ref: ${ref10}`);
        return {};
    }
    const { storeFallbackImage , offerFallbackImage , productCategoryFallbackImage , paymentIcons , socialMediaIcons , storeDetailCampaign ,  } = content;
    return {
        storeFallbackImage: storeFallbackImage === null || storeFallbackImage === void 0 ? void 0 : storeFallbackImage.url,
        offerFallbackImage: offerFallbackImage === null || offerFallbackImage === void 0 ? void 0 : offerFallbackImage.url,
        productCategoryFallbackImage: productCategoryFallbackImage === null || productCategoryFallbackImage === void 0 ? void 0 : productCategoryFallbackImage.url,
        productCategories,
        paymentIcons,
        socialMediaIcons,
        campaignBanner: {
            description: campaignBannerContent === null || campaignBannerContent === void 0 ? void 0 : campaignBannerContent.description,
            url: campaignBannerContent === null || campaignBannerContent === void 0 ? void 0 : (ref5 = campaignBannerContent.original) === null || ref5 === void 0 ? void 0 : (ref6 = ref5._links) === null || ref6 === void 0 ? void 0 : (ref7 = ref6.site) === null || ref7 === void 0 ? void 0 : ref7.href,
            startDate: storeDetailCampaign === null || storeDetailCampaign === void 0 ? void 0 : (ref8 = storeDetailCampaign.startEndDate) === null || ref8 === void 0 ? void 0 : ref8.startDate,
            endDate: storeDetailCampaign === null || storeDetailCampaign === void 0 ? void 0 : (ref9 = storeDetailCampaign.startEndDate) === null || ref9 === void 0 ? void 0 : ref9.endDate,
            storesList: storeDetailCampaign === null || storeDetailCampaign === void 0 ? void 0 : storeDetailCampaign.storesList
        }
    };
};

const useBloomreachCartConfiguration = ()=>{
    var ref, ref1, ref2;
    const { pageModel  } = useBloomreach();
    const page = useBrPageContext();
    const ref3 = (ref = pageModel === null || pageModel === void 0 ? void 0 : pageModel.page.models) === null || ref === void 0 ? void 0 : (ref1 = ref.cartConfiguration) === null || ref1 === void 0 ? void 0 : ref1.$ref;
    const content1 = getContentByRef(ref3, pageModel);
    const { title: title1 , description , linkConfigurator1 , linkConfigurator2 , policyItem  } = content1 !== null && content1 !== void 0 ? content1 : {};
    const firstLink = parselinkConfigurator(linkConfigurator1, page);
    const secondLink = parselinkConfigurator(linkConfigurator2, page);
    const policyItems = policyItem && (policyItem === null || policyItem === void 0 ? void 0 : policyItem.length) > 0 ? policyItem === null || policyItem === void 0 ? void 0 : policyItem.map(({ text , url , popupConfig: { content , title  } , icon  })=>({
            text,
            url,
            popupConfig: {
                content,
                title
            },
            icon
        })
    ) : undefined;
    if (!(pageModel === null || pageModel === void 0 ? void 0 : (ref2 = pageModel.page) === null || ref2 === void 0 ? void 0 : ref2.models) || !content1) {
        console.warn(`Cannot find main content for Cart Configuration. Got: ${content1} for $ref: ${ref3}`);
        return;
    }
    return {
        title: title1 || '',
        description: description || '',
        firstLink,
        secondLink,
        policyItems: policyItems || undefined
    };
};

var NewsletterSubscribeStatus;
(function(NewsletterSubscribeStatus) {
    NewsletterSubscribeStatus["FAILURE"] = "FAILURE";
    NewsletterSubscribeStatus["SUCCESS"] = "SUCCESS";
})(NewsletterSubscribeStatus || (NewsletterSubscribeStatus = {}));
var UIType;
(function(UIType) {
    UIType["CHECKBOX"] = "CHECKBOX";
})(UIType || (UIType = {}));

const SIGNUP_PROMOTION = gql`
  mutation SignupPromotion($signupPromotionInput: SignupPromotionInput!) {
    signupPromotion(signupPromotionInput: $signupPromotionInput) {
      campaignId
      success
      errorMessage
    }
  }
`;

const NEWSLETTER_SUBSCRIBE = gql`
  mutation NewsletterSubscribe($input: NewsletterSubscribeInput!) {
    newsletterSubscribe(input: $input) {
      __typename
      status
      failures {
        ... on Failure {
          __typename
          message
        }
        ... on UnknownFailure {
          __typename
          message
        }
        ... on NetworkFailure {
          __typename
          message
        }
        ... on AuthorizationFailure {
          __typename
          message
        }
      }
    }
  }
`;

const useNewsletterSubscribe = ()=>{
    const [newsletterSubscribe, { data , error: error1 , loading  }] = useMutation(NEWSLETTER_SUBSCRIBE, {
        onError: (error)=>{
            console.error(`Error occurred while executing the newsletter mutation: ${error}`);
        }
    });
    const { failures , status  } = (data === null || data === void 0 ? void 0 : data.newsletterSubscribe) || {};
    /**
   * The `AuthorizationFailure` failure occurs when the logged in user tries to submit a different email address.
   */ const hasAuthorizationFailure = !!(failures === null || failures === void 0 ? void 0 : failures.find((failure)=>failure.__typename === 'AuthorizationFailure'
    ));
    const onSubmit = ({ email  })=>{
        newsletterSubscribe({
            variables: {
                input: {
                    email
                }
            }
        });
    };
    return {
        hasAuthorizationFailure,
        hasError: !!error1 || !!(failures === null || failures === void 0 ? void 0 : failures.length),
        hasSubscribed: status === NewsletterSubscribeStatus.SUCCESS,
        isLoading: loading,
        onSubmit
    };
};

const useBloomreachNewsletterData = ()=>{
    var ref, ref1;
    const newsletterSubscribe = useNewsletterSubscribe();
    /**
   * Get the newsletter content from the BR Newsletter static document
   */ const { pageModel  } = useBloomreach();
    const ref2 = (ref = pageModel === null || pageModel === void 0 ? void 0 : pageModel.page.models) === null || ref === void 0 ? void 0 : (ref1 = ref.newsletterConfiguration) === null || ref1 === void 0 ? void 0 : ref1.$ref;
    const { richTextAfter , richTextBefore , title  } = getContentByRef(ref2, pageModel) || {};
    return swcHelpers.objectSpread({}, newsletterSubscribe, {
        contentAfterSubmit: richTextAfter === null || richTextAfter === void 0 ? void 0 : richTextAfter.value,
        contentBeforeSubmit: richTextBefore === null || richTextBefore === void 0 ? void 0 : richTextBefore.value,
        title
    });
};

const mapButtonStyle$1 = (buttonStyle = '')=>{
    const [colorMap, variantMap] = buttonStyle.split(' ');
    const color = colorMap;
    const variant = variantMap;
    return {
        color,
        variant
    };
};
const useGetCtaButtonComponent = ()=>{
    var ref, ref1;
    const { pageModel  } = useBloomreach();
    if (!pageModel) {
        return {
            contentBlock: undefined
        };
    }
    const mainComponent = getComponentByPath('main/content', pageModel);
    const ref2 = mainComponent === null || mainComponent === void 0 ? void 0 : (ref = mainComponent.models) === null || ref === void 0 ? void 0 : (ref1 = ref.document) === null || ref1 === void 0 ? void 0 : ref1.$ref;
    const { ctabuttonCompound  } = getContentByRef(ref2, pageModel) || {};
    const contentBlock = ctabuttonCompound;
    const { color , variant  } = mapButtonStyle$1(contentBlock === null || contentBlock === void 0 ? void 0 : contentBlock.buttonStyling);
    return {
        contentBlock,
        color,
        variant
    };
};

function withBloomreachData(Component) {
    const displayName = getDisplayName(Component);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const WithBloomreachData = (props)=>{
        const { ref , data  } = useBloomreachData(props);
        if (!data || !data.content) {
            console.error(`Tried to render CmsComponent ${displayName} without content object with ref: ${ref}`);
            return /*#__PURE__*/ React.createElement(Component, swcHelpers.extends({}, props));
        }
        return /*#__PURE__*/ React.createElement(Component, swcHelpers.extends({}, data, props));
    };
    WithBloomreachData.displayName = `WithBloomreachData(${displayName})`;
    return WithBloomreachData;
}

function withBloomreachPageable(Component) {
    const displayName = getDisplayName(Component);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const WithBloomreachPageable = (props)=>{
        var ref;
        const { refs , data  } = useBloomreachPageable(props);
        if (!((ref = data.content.items) === null || ref === void 0 ? void 0 : ref.length)) {
            console.warn(`Trying to render Bloomreach pageable component: ${displayName}, but found no content documents. Refs: ${refs}`);
            return /*#__PURE__*/ React.createElement(Component, swcHelpers.extends({}, props, {
                content: {
                    items: []
                }
            }));
        }
        return /*#__PURE__*/ React.createElement(Component, swcHelpers.extends({}, data, props));
    };
    WithBloomreachPageable.displayName = `WithBloomreachPageable(${displayName})`;
    return WithBloomreachPageable;
}

function withBreakpointVisibility(Component) {
    const displayName = getDisplayName(Component);
    const WithBreakpointVisibility = (props)=>{
        return useBreakpointVisibility(props) ? /*#__PURE__*/ React.createElement(Component, swcHelpers.extends({}, props)) : null;
    };
    WithBreakpointVisibility.displayName = `WithBreakpointVisibility(${displayName})`;
    return WithBreakpointVisibility;
}

/**
 * This HoC requires the passed component to have a content object on its props
 * This means when used in combination with withBloomreachData hoc, this hoc should come after
 *  compose(withBloomreachData, withEditableContent)
 */ function withEditableContent(Component) {
    const displayName = getDisplayName(Component);
    const WithEditableContent = (props)=>{
        if (!props.content || !props.content.document) {
            return /*#__PURE__*/ React.createElement(Component, swcHelpers.extends({}, props));
        }
        return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", {
            style: {
                position: 'relative'
            }
        }, /*#__PURE__*/ React.createElement(BrManageContentButton, {
            content: props.content.document
        })), /*#__PURE__*/ React.createElement(Component, swcHelpers.extends({}, props)));
    };
    WithEditableContent.displayName = `WithEditableContent(${displayName})`;
    return WithEditableContent;
}

/**
 * With this HoC, CmsContainers can be hidden by content editors
 * for specific audiences using Br's relevance module.
 * Only works for Bloomreach components of type CONTAINER_ITEM_COMPONENT
 */ function withHidableContent(Component) {
    const displayName = getDisplayName(Component);
    const WithHidableContent = (props)=>{
        const { component , page  } = props || {};
        const preview = page === null || page === void 0 ? void 0 : page.isPreview();
        const hidden = component && isContainerItem(component) ? component.isHidden() : false;
        // A Cms component should always be shown in preview mode
        if (hidden && !preview) {
            return null;
        }
        return /*#__PURE__*/ React.createElement(Component, swcHelpers.extends({}, props));
    };
    WithHidableContent.displayName = `withHidableContent(${displayName})`;
    return WithHidableContent;
}

const CmsAccordionItem = withEditableContent(({ content , expanded , headingLevel  })=>{
    var ref;
    const richText = useBloomreachRichText((ref = content.content) === null || ref === void 0 ? void 0 : ref.value);
    return /*#__PURE__*/ React.createElement(AccordionItem, {
        id: content.id,
        label: content.title,
        expanded: expanded,
        headingLevel: headingLevel,
        fullWidth: true
    }, /*#__PURE__*/ React.createElement(RichTextArea, {
        content: richText
    }));
});
const CmsAccordion = ({ headingLevel , meta , content  })=>/*#__PURE__*/ React.createElement(Accordion, null, content.items.map((item, i)=>/*#__PURE__*/ React.createElement(CmsAccordionItem, {
            key: item.id,
            content: item,
            headingLevel: headingLevel,
            expanded: meta.expandedByDefault === `${i + 1}`
        })
    ))
;
var CmsAccordion$1 = compose(withBreakpointVisibility, withHidableContent, withBloomreachPageable, withAnchor)(CmsAccordion);

const CmsArticle = ({ component  })=>{
    const { document: article  } = component.getModels();
    const { template  } = component.getParameters();
    const image = parseBloomreachImage(article === null || article === void 0 ? void 0 : article.image);
    var ref;
    const [link] = useBloomreachLinks([
        {
            url: (ref = article === null || article === void 0 ? void 0 : article.url) !== null && ref !== void 0 ? ref : '#'
        }
    ]);
    if (!article) {
        return null;
    }
    switch(template){
        case 'article-banner-preview':
            return /*#__PURE__*/ React.createElement(Article$1, null, image && /*#__PURE__*/ React.createElement(Banner, {
                useContainer: true,
                image: image.original,
                mobileImage: image.mobile,
                link: link
            }));
        default:
            return /*#__PURE__*/ React.createElement(Heading$1, {
                variant: "subheading1"
            }, `Unsupported article template ${template}`);
    }
};
var Article = compose(withAnchor, withBreakpointVisibility)(CmsArticle);

const CmsArticleCollectionItem = withEditableContent(({ content  })=>{
    const { coverImage , url , title , summary  } = content;
    const [link] = useBloomreachLinks([
        {
            url: url !== null && url !== void 0 ? url : '#'
        }
    ]);
    const image = parseBloomreachImage(coverImage);
    if (!image) {
        console.error('Missing coverImage');
        return null;
    }
    return /*#__PURE__*/ React.createElement(HighlightedContentItem, {
        image: image.original,
        text: summary,
        title: title,
        link: link
    });
});
const CmsArticleCollection = ({ content  })=>/*#__PURE__*/ React.createElement(HighlightedContent, null, content.items.map((item)=>/*#__PURE__*/ React.createElement(CmsArticleCollectionItem, {
            key: item.id,
            content: item
        })
    ))
;
var CmsArticleCollection$1 = compose(withBreakpointVisibility, withHidableContent, withBloomreachPageable, withEditableContent, withAnchor)(CmsArticleCollection);

const mapConsents = (selectedConsents)=>selectedConsents.map((consent)=>({
            consentName: consent.title,
            literalText: consent.title,
            uiType: UIType.CHECKBOX,
            value: true
        })
    )
;
const mapOptIns = (optIns)=>optIns.map((optIn)=>({
            id: optIn.value,
            value: true,
            literalText: optIn.title,
            uiType: UIType.CHECKBOX
        })
    )
;
var useSignupCampaign = ()=>{
    var ref, ref1, ref2, ref3;
    const [signupCampaignMutation, { data , loading , error  }] = useMutation(SIGNUP_PROMOTION);
    const callback = (_param)=>{
        var { consents , optIns , personalDetails  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
            "consents",
            "optIns",
            "personalDetails"
        ]);
        return signupCampaignMutation({
            variables: {
                signupPromotionInput: swcHelpers.objectSpread({
                    consents: mapConsents(consents),
                    optIns: mapOptIns(optIns),
                    personalDetails: {
                        firstName: personalDetails.firstName,
                        middleName: personalDetails.middleName,
                        lastName: personalDetails.lastName,
                        email: personalDetails.email
                    }
                }, rest)
            }
        });
    };
    return [
        callback,
        {
            campaignId: (data === null || data === void 0 ? void 0 : (ref = data.signupPromotion) === null || ref === void 0 ? void 0 : ref.success) ? data === null || data === void 0 ? void 0 : (ref1 = data.signupPromotion) === null || ref1 === void 0 ? void 0 : ref1.campaignId : '',
            loading,
            error: (data === null || data === void 0 ? void 0 : (ref2 = data.signupPromotion) === null || ref2 === void 0 ? void 0 : ref2.errorMessage) || null,
            success: !!(data === null || data === void 0 ? void 0 : (ref3 = data.signupPromotion) === null || ref3 === void 0 ? void 0 : ref3.success)
        }, 
    ];
};

const getItemIdentifier = (item)=>item.value
;
const getItemErrorMessage = (item)=>{
    return (item === null || item === void 0 ? void 0 : item.errorMessage) || '';
};
var RichBannerCampaign = (_param)=>{
    var { onSubmit , onChange , formState , optIns , selectedOptIns , setSelectedOptIns , consents , selectedConsents , setSelectedConsents , loading  } = _param, props = swcHelpers.objectWithoutProperties(_param, [
        "onSubmit",
        "onChange",
        "formState",
        "optIns",
        "selectedOptIns",
        "setSelectedOptIns",
        "consents",
        "selectedConsents",
        "setSelectedConsents",
        "loading"
    ]);
    const { getLabel , getFormattedLabel  } = useIntl();
    return /*#__PURE__*/ React.createElement(RichBanner, swcHelpers.extends({}, props), /*#__PURE__*/ React.createElement(Form, {
        onSubmit: onSubmit,
        name: "campaign",
        "data-t": "campaign-form",
        initialFormState: formState,
        onChange: onChange
    }, /*#__PURE__*/ React.createElement("div", {
        className: "campaign-form"
    }, /*#__PURE__*/ React.createElement(FirstName, {
        required: true,
        label: getLabel('checkout.contact.firstName'),
        errorMessage: getLabel('checkout.contact.firstName.errorMessage')
    }), /*#__PURE__*/ React.createElement(MiddleName, {
        required: true,
        label: getLabel('checkout.contact.middleName'),
        errorMessage: getLabel('checkout.contact.middleName.errorMessage')
    }), /*#__PURE__*/ React.createElement(LastName, {
        required: true,
        label: getLabel('checkout.contact.lastName'),
        errorMessage: getLabel('checkout.contact.lastName.errorMessage')
    }), /*#__PURE__*/ React.createElement(Email, {
        label: getLabel('fields.input.email.label'),
        errorMessage: getLabel('account.error.emailInvalid'),
        required: true
    })), !!optIns && !!optIns.length && /*#__PURE__*/ React.createElement(CheckboxList, {
        idPrefix: "opt-in__",
        items: optIns,
        onChange: setSelectedOptIns,
        selectedItems: selectedOptIns,
        getItemIdentifier: (optIn)=>optIn.titleKey
        ,
        renderItem: (item)=>/*#__PURE__*/ React.createElement("span", null, getFormattedLabel(item.titleKey, {
                parseHTML: true
            }))
    }), /*#__PURE__*/ React.createElement(CheckboxList, {
        getItemIdentifier: getItemIdentifier,
        getItemErrorMessage: getItemErrorMessage,
        // TODO shouldItemValidate: when form is touched
        idPrefix: "consents__",
        items: consents,
        onChange: setSelectedConsents,
        renderItem: (item)=>getFormattedLabel(item.titleKey)
        ,
        selectedItems: selectedConsents
    }), /*#__PURE__*/ React.createElement(Button, {
        "data-t": "submit-signup-campaign",
        type: "submit",
        isLoading: loading,
        caption: getLabel('campaign.signup.submit'),
        className: "login__button"
    })));
};

const consents = [
    {
        titleKey: 'campaign.signup.consent',
        value: 'false',
        shouldItemValidate: false
    }, 
];
const optIns = [
    {
        titleKey: 'campaign.signup.optIn',
        value: 'false'
    }, 
];
const addFullTitleToItems = (getFormattedLabel, items)=>items.map((item)=>swcHelpers.objectSpread({}, item, {
            title: getFormattedLabel(item.titleKey)
        })
    )
;
const RichBannerCampaignContainer = (props)=>{
    const { getFormattedLabel  } = useIntl();
    const [selectedOptIns, setSelectedOptIns] = React.useState([]);
    const [selectedConsents, setSelectedConsents] = React.useState([]);
    const [formState, setFormState] = React.useState(Util.fieldsToFormState({
        firstName: '',
        middleName: '',
        lastName: '',
        email: ''
    }));
    const [signupCampaign, { loading , error , success  }] = useSignupCampaign();
    const handleSubmit = React.useCallback((state)=>{
        const { firstName , middleName , lastName , email  } = Util.getValuesFromState(state.formState);
        if (!(firstName && middleName && lastName && email)) {
            return;
        }
        signupCampaign({
            consents: addFullTitleToItems(getFormattedLabel, selectedConsents),
            optIns: addFullTitleToItems(getFormattedLabel, selectedOptIns),
            personalDetails: {
                firstName,
                middleName,
                lastName,
                email
            },
            campaignId: props.campaignId
        });
    }, [
        signupCampaign,
        selectedConsents,
        selectedOptIns,
        props.campaignId
    ]);
    const handleChange = (newState)=>setFormState(newState.formState)
    ;
    React.useEffect(()=>{
        if (error) {
            if (error === 'Signup to promotion failed due to missing consent.' /*'MISSING_CONSENT'*/ ) {
                toast.failed('campaign.signup.missing_consent');
            } else {
                toast.failed('campaign.signup.general_error');
            }
        } else if (success) {
            toast.success('campaign.signup.success');
            setFormState({});
        }
    }, [
        loading
    ]);
    return /*#__PURE__*/ React.createElement(RichBannerCampaign, swcHelpers.extends({}, props, {
        onSubmit: handleSubmit,
        formState: formState,
        onChange: handleChange,
        optIns: optIns,
        selectedOptIns: selectedOptIns,
        setSelectedOptIns: setSelectedOptIns,
        consents: consents,
        selectedConsents: selectedConsents,
        setSelectedConsents: setSelectedConsents,
        loading: loading
    }));
};

const RichBannerEyeExam = (props)=>{
    const eyeExamDataLayer = React.useMemo(()=>({
            onLogin: ()=>pushLoginAction('easee page')
            ,
            onConsent: (hasConsent)=>hasConsent && pushCheckedConsent('easee page')
            ,
            onStartEyeExam: ()=>pushStartEyeTest('easee page')
        })
    , []);
    return /*#__PURE__*/ React.createElement(RichBanner, swcHelpers.extends({}, props), /*#__PURE__*/ React.createElement(LaunchEyeExam, swcHelpers.extends({}, eyeExamDataLayer)));
};

/**
 * The RuntimeContext is deprecated and will be removed in the future as it's too generic,
 * we should supply props trough more specific contexts.
 *
 * Please do not add this component in any new components outside this package or extend the current model
 */ const RuntimeContext = React.createContext({});

const CmsBanner = ({ content , template , meta  })=>{
    const { title , description , content: richText  } = content;
    const { siteLogos  } = useBloomreach();
    const [_, { formatProductUrl  }] = useBloomreachProductRoutes();
    const { grandvision  } = React.useContext(RuntimeContext);
    const links = useBloomreachLinks(content.links);
    const image = parseBloomreachImage(content.image);
    switch(template){
        case 'full-width':
            return /*#__PURE__*/ React.createElement(Banner, {
                image: image === null || image === void 0 ? void 0 : image.original,
                mobileImage: image === null || image === void 0 ? void 0 : image.mobile,
                link: links[0]
            });
        case 'contained-banner':
            return /*#__PURE__*/ React.createElement(Banner, {
                image: image === null || image === void 0 ? void 0 : image.original,
                mobileImage: image === null || image === void 0 ? void 0 : image.mobile,
                link: links[0],
                useContainer: true
            });
        case 'top-banner':
            return /*#__PURE__*/ React.createElement(Banner, {
                image: image === null || image === void 0 ? void 0 : image.original,
                mobileImage: image === null || image === void 0 ? void 0 : image.mobile,
                link: links[0],
                useContainer: true,
                useLazyLoading: false
            });
        case 'content-banner':
            return /*#__PURE__*/ React.createElement(ContentBanner, {
                image: image === null || image === void 0 ? void 0 : image.original,
                mobileImage: image === null || image === void 0 ? void 0 : image.mobile,
                links: links,
                title: title,
                text: description,
                textColor: meta.textColor
            });
        case 'rich-banner':
            var ref;
            const products = (ref = content === null || content === void 0 ? void 0 : content.products) !== null && ref !== void 0 ? ref : [];
            return /*#__PURE__*/ React.createElement(RichBanner, swcHelpers.extends({}, meta, {
                image: image === null || image === void 0 ? void 0 : image.original,
                mobileImage: image === null || image === void 0 ? void 0 : image.mobile,
                richText: richText === null || richText === void 0 ? void 0 : richText.value,
                link: links.find((l)=>l.style === 'image'
                )
            }), products.length > 0 && /*#__PURE__*/ React.createElement(ProductBlocksContainer, {
                skus: products.length > 6 ? products.slice(0, 6) : products,
                limit: 2,
                staticsUrl: grandvision === null || grandvision === void 0 ? void 0 : grandvision.staticsUrl,
                formatProductUrl: formatProductUrl,
                reimbursableLogos: {
                    default: siteLogos.reimbursable,
                    alternative: siteLogos.reimbursableAlternative
                }
            }));
        case 'rich-banner-with-eye-exam-cta':
            return /*#__PURE__*/ React.createElement(RichBannerEyeExam, swcHelpers.extends({}, meta, {
                image: image === null || image === void 0 ? void 0 : image.original,
                mobileImage: image === null || image === void 0 ? void 0 : image.mobile,
                richText: richText === null || richText === void 0 ? void 0 : richText.value,
                link: links.find((l)=>l.style === 'image'
                )
            }));
        case 'rich-banner-with-campaign':
            if (!content.campaignId) {
                return /*#__PURE__*/ React.createElement(Heading$1, {
                    variant: "subheading1"
                }, "Campaign Banner rendered without campaignId - please add it in BloomReach.");
            }
            return /*#__PURE__*/ React.createElement(RichBannerCampaignContainer, swcHelpers.extends({}, meta, {
                campaignId: content.campaignId,
                image: image === null || image === void 0 ? void 0 : image.original,
                mobileImage: image === null || image === void 0 ? void 0 : image.mobile,
                richText: richText === null || richText === void 0 ? void 0 : richText.value,
                link: links.find((l)=>l.style === 'image'
                )
            }));
        default:
            return /*#__PURE__*/ React.createElement(Heading$1, {
                variant: "subheading1"
            }, "Banner with template: ", template, " is not defined in CmsBanner component.");
    }
};
var CmsBanner$1 = compose(withBreakpointVisibility, withBloomreachData, withEditableContent, withAnchor)(CmsBanner);

const CmsBannerCollectionItem = withEditableContent(({ content , template  })=>{
    const { title , description , content: richText  } = content;
    const image = parseBloomreachImage(content.image);
    const links = useBloomreachLinks(content.links);
    switch(template){
        case 'certificates':
            return /*#__PURE__*/ React.createElement(CertificatesItem, {
                image: image === null || image === void 0 ? void 0 : image.original,
                link: links[0],
                title: title,
                text: description
            });
        case 'main-categories':
            return /*#__PURE__*/ React.createElement(CategoryLinksItem, {
                image: image === null || image === void 0 ? void 0 : image.original,
                link: links[0],
                title: title
            });
        case 'usp':
            return /*#__PURE__*/ React.createElement(HighlightedContentItem, {
                image: image === null || image === void 0 ? void 0 : image.original,
                link: links[0],
                title: title,
                text: description
            });
        case 'promotion-banner':
        case 'split-header-promotion':
            return /*#__PURE__*/ React.createElement(PromotionBannersItem, {
                image: image === null || image === void 0 ? void 0 : image.original,
                mobileImage: image === null || image === void 0 ? void 0 : image.mobile,
                link: links[0],
                title: title,
                text: description,
                fullWidth: template === 'split-header-promotion'
            });
        case 'rich-banner':
            return /*#__PURE__*/ React.createElement(RichBanner, {
                image: image === null || image === void 0 ? void 0 : image.original,
                mobileImage: image === null || image === void 0 ? void 0 : image.mobile,
                richText: richText === null || richText === void 0 ? void 0 : richText.value,
                link: links.find((l)=>l.style === 'image'
                ),
                backgroundColor: "white",
                textColor: "black",
                bannerWidth: "full",
                layout: "collection"
            });
        default:
            return null;
    }
});
const CmsBannerCollection = ({ content , template , meta  })=>{
    const isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    const items = React.useMemo(()=>content.items.map((item, i)=>/*#__PURE__*/ React.createElement(CmsBannerCollectionItem, {
                key: `${item.id}-${i}`,
                content: item,
                template: template
            })
        )
    , [
        content.items,
        template
    ]);
    switch(template){
        case 'certificates':
            return /*#__PURE__*/ React.createElement(Certificates, null, items);
        case 'main-categories':
            return /*#__PURE__*/ React.createElement(CategoryLinks, null, items);
        case 'usp':
            return /*#__PURE__*/ React.createElement(HighlightedContent, null, items);
        case 'promotion-banner':
        case 'split-header-promotion':
            return /*#__PURE__*/ React.createElement(PromotionBanners, {
                fullWidth: template === 'split-header-promotion'
            }, items);
        case 'rich-banner':
            return /*#__PURE__*/ React.createElement(Container, null, isSmallScreen && meta.mobilePresentation === 'horizontal' ? /*#__PURE__*/ React.createElement(SliderCarousel, null, items) : /*#__PURE__*/ React.createElement(FlexLayout, {
                justify: "space-between",
                wrap: "wrap"
            }, items));
        default:
            return /*#__PURE__*/ React.createElement(Heading$1, {
                variant: "subheading1"
            }, `Banner collection with template: ${template} is not defined in CmsBannerCollection component.`);
    }
};
var CmsBannerCollection$1 = compose(withBloomreachPageable, withBreakpointVisibility, withAnchor)(CmsBannerCollection);

const CmsBlogCollectionItem = withEditableContent(({ content  })=>{
    const { title , summary , url  } = content;
    const coverImage = parseBloomreachImage(content.coverImage);
    const [link] = useBloomreachLinks([
        {
            url: url !== null && url !== void 0 ? url : '#'
        }
    ]);
    if (!coverImage) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(HighlightedContentItem, {
        image: coverImage.original,
        title: title,
        text: summary,
        link: link
    });
});
const CmsBlogCollection = ({ content  })=>/*#__PURE__*/ React.createElement(HighlightedContent, null, content.items.map((item)=>/*#__PURE__*/ React.createElement(CmsBlogCollectionItem, {
            key: item.id,
            content: item
        })
    ))
;
var BlogCollection = compose(withBreakpointVisibility, withHidableContent, withBloomreachPageable, withAnchor)(CmsBlogCollection);

const CmsBlogOverviewItem = ({ content  })=>{
    const { locale , getLabel  } = useIntl();
    const { title , coverImage , readingTime , publishDate , summary , url  } = content;
    const [link] = useBloomreachLinks([
        {
            url: url !== null && url !== void 0 ? url : '#'
        }
    ]);
    const image = parseBloomreachImage(coverImage);
    return /*#__PURE__*/ React.createElement(BlogCard, {
        url: link.url,
        title: title,
        summary: summary,
        readingTime: readingTime,
        publishDate: publishDate,
        image: image === null || image === void 0 ? void 0 : image.original,
        mobileImage: image === null || image === void 0 ? void 0 : image.mobile,
        locale: locale,
        minutesLabel: getLabel('blog.minutesToRead')
    });
};
const CmsBlogHero = ({ content  })=>{
    const { getLabel  } = useIntl();
    const { coverImage , title , url , summary  } = content;
    const [link] = useBloomreachLinks([
        {
            url: url !== null && url !== void 0 ? url : '#'
        }
    ]);
    const image = parseBloomreachImage(coverImage);
    if (!content || !image) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(BlogHero, {
        title: title,
        link: link.url,
        summary: summary,
        image: image.original,
        mobileImage: image.mobile,
        readMoreLabel: getLabel('blog.readArticle')
    });
};
const CmsBlogOverview = ({ component , page  })=>{
    const { getLabel  } = useIntl();
    const { title ='Blog'  } = component.getParameters();
    const { loading , error , result , loadNextPage  } = useBloomreachArticles({
        component,
        page
    });
    if (error) {
        console.error('CmsBlogOverview: Failed to fetch more articles from Bloomreach.', error);
    }
    const { highlighted , paged  } = result;
    const { currentPage , nextPage , totalPages  } = paged;
    const hasMoreItems = nextPage !== null && nextPage !== currentPage && nextPage <= totalPages;
    if (!paged.items) {
        console.error('CmsBlogOverview: No blog items found to render.');
        return null;
    }
    return /*#__PURE__*/ React.createElement(React.Fragment, null, highlighted && /*#__PURE__*/ React.createElement(CmsBlogHero, {
        content: highlighted
    }), /*#__PURE__*/ React.createElement(BlogList, {
        title: title,
        noArticlesLabel: getLabel('blog.noArticles')
    }, paged.items.map((item)=>item && /*#__PURE__*/ React.createElement(CmsBlogOverviewItem, {
            key: item.id,
            content: item
        })
    )), hasMoreItems && /*#__PURE__*/ React.createElement("div", {
        className: "blog-list__show-more"
    }, /*#__PURE__*/ React.createElement(Button, {
        className: "blog-list__button",
        variant: "outline",
        color: "primary",
        isLoading: loading,
        href: `?page=${result.paged.nextPage}`,
        onClick: (e)=>{
            e.preventDefault();
            loadNextPage();
            if (result.paged.nextPage) {
                const url = new URL(window.location.href);
                url.searchParams.set('page', result.paged.nextPage.toString());
                window.history.pushState({}, '', url);
            }
        },
        caption: getLabel('blog.articles.showMore')
    })));
};
var BlogOverview = compose(withBreakpointVisibility, withAnchor)(CmsBlogOverview);

const CmsExclusiveBrandsItem = withEditableContent(({ content  })=>{
    const [link] = useBloomreachLinks([
        {
            url: content.url
        }
    ]);
    if (!content.brandLogo) {
        return null;
    }
    return /*#__PURE__*/ React__default.createElement(ExclusiveBrandsItem, {
        url: link.url,
        title: content.title,
        logoSrc: content.brandLogo.url
    });
});
const CmsBrandCollection = ({ content , template  })=>{
    switch(template){
        case 'exclusive-brands':
            return /*#__PURE__*/ React__default.createElement(ExclusiveBrands, null, content.items.map((brand)=>/*#__PURE__*/ React__default.createElement(CmsExclusiveBrandsItem, {
                    key: brand.id,
                    content: brand
                })
            ));
        default:
            return /*#__PURE__*/ React__default.createElement(Heading$1, {
                variant: "subheading1"
            }, "Banner of type ", template, " not defined in BrandCollection");
    }
};
var BrandCollection = compose(withBreakpointVisibility, withHidableContent, withBloomreachPageable, withAnchor)(CmsBrandCollection);

const CmsBrandListItem = withEditableContent(({ content  })=>{
    var _url;
    const [link] = useBloomreachLinks([
        {
            url: (_url = content.url) !== null && _url !== void 0 ? _url : '#'
        }
    ]);
    return /*#__PURE__*/ React__default.createElement(BrandListItem, {
        url: link.url
    }, content.title);
});
const CmsBrandList = ({ content , meta , template  })=>{
    const { locale  } = useIntl();
    switch(template){
        case 'brand-list':
            const groups = groupBrands(content.items, 4, locale);
            return /*#__PURE__*/ React__default.createElement(BrandList$1, {
                title: String(meta.title)
            }, groups.map(({ title , items  }, i)=>/*#__PURE__*/ React__default.createElement(BrandListGroup, {
                    title: title,
                    key: i
                }, items.map((brand)=>/*#__PURE__*/ React__default.createElement(CmsBrandListItem, {
                        content: brand,
                        key: brand.id
                    })
                ))
            ));
        default:
            return /*#__PURE__*/ React__default.createElement(Heading$1, {
                variant: "subheading1"
            }, "Banner of type ", template, " not defined in BrandList");
    }
};
var BrandList = compose(withBreakpointVisibility, withHidableContent, withBloomreachPageable, withAnchor)(CmsBrandList);

const CmsCarouselItem = withEditableContent(({ content  })=>{
    const [link] = useBloomreachLinks(content.links);
    const image = parseBloomreachImage(content.image);
    if (!image) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(Banner, {
        image: image.original,
        mobileImage: image.mobile,
        link: link
    });
});
const CmsCarousel = ({ content  })=>{
    const { preview  } = useBloomreach();
    return /*#__PURE__*/ React.createElement(Carousel, {
        autoLoop: !preview
    }, content.items.map((item)=>/*#__PURE__*/ React.createElement(CmsCarouselItem, {
            key: item.id,
            content: item
        })
    ));
};
var CmsCarousel$1 = compose(withBreakpointVisibility, withBloomreachPageable, withAnchor)(CmsCarousel);

/**
 * This component wraps the component from the SDK that renders
 * any container-component from bloomreach based on the passed path.
 *
 * Any components inside this container will be rendered based on the
 * ComponentDefinitions we pass to SDK's page component.
 *
 * This component can be used to place certain containers in a page layout.
 * @example usage
 *   <Page>
 *     <CmsComponentSlot path="main/top-container" />
 *     <Cart />
 *     <SupportFooter />
 *     <CmsComponentSlot path="main/bottom-container" />
 *   </Page>
 */ const CmsComponentSlot = ({ path ='main' , children  })=>{
    return /*#__PURE__*/ React.createElement(BrComponent, {
        path: path,
        children: children
    });
};

const CustomPackage = ({ meta  })=>{
    const { customComponentMap  } = useBloomreach();
    const element = customComponentMap && customComponentMap[meta.packageId];
    if (!element) {
        return /*#__PURE__*/ React.createElement("div", null, `No component found for Custom Package ID: "${meta.packageId}"`);
    }
    return /*#__PURE__*/ React.createElement(element.type, swcHelpers.objectSpread({}, element.props));
};
var CmsCustomPackage = compose(withBreakpointVisibility, withHidableContent, withBloomreachData, withAnchor)(CustomPackage);

const CmsIframe = ({ component  })=>{
    const { iframe: content  } = component.getModels();
    if (!content) {
        return null;
    }
    const { source , scrolling , height , position , title  } = content;
    // TODO: bloomreach needs to be updated to have a required `title` field. For now, fallback to `source`
    return /*#__PURE__*/ React.createElement(IFrame, {
        source: source,
        scrolling: scrolling,
        height: height,
        position: position,
        title: title !== null && title !== void 0 ? title : source
    });
};
var CmsIframe$1 = compose(withBreakpointVisibility, withHidableContent, withAnchor)(CmsIframe);

const CmsLinkList = ({ content  })=>{
    const links = useBloomreachLinks(content.iconLinkCompoundList.map(({ link  })=>swcHelpers.objectSpread({}, link, {
            url: link.url || '#'
        })
    ));
    return /*#__PURE__*/ React__default.createElement(Container, null, /*#__PURE__*/ React__default.createElement(ContentTabs, {
        links: links
    }));
};
var CmsLinkList$1 = compose(withBreakpointVisibility, withBloomreachData, withEditableContent, withAnchor)(CmsLinkList);

const CmsRecommendedProducts = ({ meta  })=>{
    var ref;
    const { grandvision  } = React__default.useContext(RuntimeContext);
    const { getLabel  } = useIntl();
    const [_, { formatProductUrl  }] = useBloomreachProductRoutes();
    const brcontext = useBloomreach();
    return /*#__PURE__*/ React__default.createElement(FeaturedProducts, {
        formatProductUrl: formatProductUrl,
        skus: meta.recommendedProducts || [],
        staticsUrl: (grandvision === null || grandvision === void 0 ? void 0 : grandvision.staticsUrl) || '',
        title: getLabel('blog.content.recommendedProducts.title'),
        renderLocation: (ref = brcontext.pageModel) === null || ref === void 0 ? void 0 : ref.page.name
    });
};
var CmsRecommendedProducts$1 = compose(withBreakpointVisibility, withEditableContent, withBloomreachData, withAnchor)(CmsRecommendedProducts);

/**
 * This variant of the Rich Text component is used on content pages
 */ const CmsRichTextDocument = ({ component  })=>{
    const { html  } = component.getModels();
    const { backgroundColor , textColor  } = component.getParameters();
    const richText = useBloomreachRichText(html);
    return /*#__PURE__*/ React.createElement("div", {
        className: "rich-text-wrapper"
    }, /*#__PURE__*/ React.createElement(RichTextArea, {
        content: richText,
        backgroundColor: backgroundColor,
        textColor: textColor
    }));
};
var CmsRichTextDocument$1 = compose(withBreakpointVisibility, withAnchor)(CmsRichTextDocument);

const CmsVideo = ({ component  })=>{
    const { coverImage  } = component.getModels();
    const { videoUrl  } = component.getParameters();
    if (!videoUrl) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(Video, {
        videoUrl: videoUrl,
        coverImage: coverImage
    });
};
var CmsVideo$1 = compose(withBreakpointVisibility, withAnchor)(CmsVideo);

const CmsWidget = ({ content , template , meta  })=>{
    const { isGpsStoresEnabled , isStoresPlacePredictionsEnabled  } = useFeatureToggles();
    const { title , links , inputField , image , content: text  } = content;
    const [link] = useBloomreachLinks(links);
    const input = parseBloomreachInputField(inputField);
    if (!input || !link) {
        return null;
    }
    switch(template){
        case 'book-eye-check':
        case 'book-appointment':
            return /*#__PURE__*/ React.createElement(BookAppointment, {
                title: title,
                link: link,
                inputField: input,
                isLocationEnabled: isGpsStoresEnabled,
                isPlacePredictionsEnabled: isStoresPlacePredictionsEnabled
            });
        case 'book-appointment-extended':
            return /*#__PURE__*/ React.createElement(BookAppointment, {
                title: title,
                link: link,
                inputField: input,
                isLocationEnabled: isGpsStoresEnabled,
                isPlacePredictionsEnabled: isStoresPlacePredictionsEnabled,
                text: text === null || text === void 0 ? void 0 : text.value,
                imageUrl: image === null || image === void 0 ? void 0 : image.url,
                backgroundColor: (meta === null || meta === void 0 ? void 0 : meta.backgroundColorHex) || (meta === null || meta === void 0 ? void 0 : meta.backgroundColor),
                textColor: meta === null || meta === void 0 ? void 0 : meta.textColor
            });
        default:
            return /*#__PURE__*/ React.createElement(Heading$1, {
                variant: "subheading1"
            }, "Widget of type: ", template, " is not defined.");
    }
};
var CmsWidget$1 = compose(withBreakpointVisibility, withHidableContent, withBloomreachData, withEditableContent, withAnchor)(CmsWidget);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const blockMap = {
    accordion: CmsAccordion$1,
    articleCollection: CmsArticleCollection$1,
    banner: CmsBanner$1,
    bannerCollection: CmsBannerCollection$1,
    carousel: CmsCarousel$1,
    customPackageComponent: CmsCustomPackage,
    iFrame: CmsIframe$1,
    linkList: CmsLinkList$1,
    recommendedProducts: CmsRecommendedProducts$1,
    video: CmsVideo$1,
    widget: CmsWidget$1,
    richText: CmsRichTextDocument$1
};
const ContentBlock = ({ contentBlock  })=>{
    const page = useBrPageContext();
    const component = mapBlockToComponent(contentBlock);
    const BlockComponent = getComponentForBlockType(contentBlock.type);
    const headingLevel = contentBlock.type === 'accordion' ? 'h4' : undefined;
    return BlockComponent && component ? /*#__PURE__*/ React.createElement(BlockComponent, {
        component: component,
        page: page,
        headingLevel: headingLevel
    }) : null;
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getComponentForBlockType(type) {
    const Component = blockMap[type];
    if (!Component) {
        console.log(`Missing Component for content block type: ${type}`);
        return;
    }
    return Component;
}
/**
 * The "ContentBlocks" implementation in the CMS used on content pages
 * is a custom modification of Bloomreach. Hence we need to map the content
 * object we receive to something for our CMS containers to a representation of
 * the "component" object they receive from the BR SDK by default.
 */ function mapBlockToComponent({ anchorId , coverImage , document , expandedByDefault , packageId , recommendedProducts , html , template , videoUrl , visibility , source , height , scrolling , imageWidth , imageAlignment , bannerWidth , textColor , backgroundColor , mobilePresentation , buttonText , buttonStyling , newWindow , iconImage , url , description  }) {
    const parsedDocument = Array.isArray(document) ? {
        pageable: {
            items: document
        }
    } : {
        document
    };
    function getModels() {
        return swcHelpers.objectSpread({}, parsedDocument, {
            coverImage,
            html,
            visibility,
            iframe: {
                source,
                height,
                scrolling
            }
        });
    }
    function getParameters() {
        return {
            anchorId,
            packageId,
            template,
            videoUrl,
            expandedByDefault,
            recommendedProducts,
            imageWidth,
            imageAlignment,
            bannerWidth,
            textColor,
            backgroundColor,
            mobilePresentation,
            buttonText,
            buttonStyling,
            newWindow,
            iconImage,
            url,
            description
        };
    }
    return {
        getModels,
        getParameters
    };
}

const CmsContentBlocks = ({ contentBlocks  })=>/*#__PURE__*/ React.createElement(React.Fragment, null, contentBlocks.map((contentBlock, i)=>/*#__PURE__*/ React.createElement(ContentBlock, {
            key: `${contentBlock.id}-${i}`,
            contentBlock: contentBlock
        })
    ))
;

const CmsContentBreadcrumbs = ({ url  })=>{
    const breadcrumbs = useBreadcrumbs(url);
    // Don't render if we have less than 2 breadcrumb items
    if (!breadcrumbs || breadcrumbs.length < 2) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(Container, null, /*#__PURE__*/ React.createElement(Breadcrumbs, {
        pages: breadcrumbs
    }));
};
function useBreadcrumbs(url = '') {
    const { getLabel , hasLabel , locale: defaultLocale  } = useIntl();
    const { routes  } = useBloomreachPageRoutes();
    const { isMultilingualEnabled  } = useFeatureToggles();
    const page = useBrPageContext();
    // Get locale from url because locale from cookies(intl) is not always up to date in preview mode
    const locale = LocaleUtils.getLocaleFromUrl(url) || defaultLocale;
    const localeStr = locale.replace('-', '_');
    const [path = '', queryString = ''] = url === null || url === void 0 ? void 0 : url.split('?');
    let segments = path.split('/');
    // If we get an "internal" bloomreach url, we need to slice of the first three items (/site/_cmsinternal/[channel])
    // so they won't show up in the visual breadcrumbs
    if ((page === null || page === void 0 ? void 0 : page.isPreview()) && path.startsWith('/site/_cmsinternal')) {
        segments = [
            ...segments
        ].filter((s)=>s !== '' && s !== localeStr
        ).slice(3, segments.length - 1);
    } else {
        segments = segments.filter((s)=>s !== '' && s !== localeStr
        );
    }
    return [
        {
            name: getLabel(`breadcrumb.breadcrumb.home`),
            url: (page === null || page === void 0 ? void 0 : page.isPreview()) ? page === null || page === void 0 ? void 0 : page.getUrl(routes.root) : routes.root
        },
        ...segments.map((currentSegment, index)=>createUrlBreadcrumb({
                currentSegment,
                segments,
                queryString,
                index,
                getLabel,
                hasLabel,
                page,
                locale: isMultilingualEnabled ? localeStr : undefined
            })
        ), 
    ];
}
function createUrlBreadcrumb({ segments , currentSegment , queryString , index , getLabel , hasLabel , page , locale  }) {
    const previous = segments.slice(0, index).join('/');
    const labelKey = `breadcrumb.breadcrumb.${currentSegment}`;
    const name = hasLabel(labelKey) ? getLabel(labelKey) : StringUtils.upFirst(currentSegment.replace(/-/g, ' '));
    let url = previous ? `/${previous}/${currentSegment}` : `/${currentSegment}`;
    if (queryString) url += `?${queryString}`;
    if (locale) {
        url = `/${locale}${url}`;
    }
    return {
        url: getUrl(url, page),
        name
    };
}

const CmsDocument = ({ content  })=>{
    var ref;
    const richText = useBloomreachRichText((ref = content.content) === null || ref === void 0 ? void 0 : ref.value);
    if (!richText) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(RichTextArea, {
        content: richText
    });
};
var Document = compose(withBreakpointVisibility, withHidableContent, withBloomreachData, withEditableContent, withAnchor)(CmsDocument);

const CmsEmpty = ({ component , page  })=>{
    const preview = page.isPreview();
    // This component should only render in CMS preview mode
    if (!preview) {
        return null;
    }
    return /*#__PURE__*/ React.createElement("div", null, component === null || component === void 0 ? void 0 : component.getName());
};

const CmsFaqItem = withEditableContent(({ content  })=>{
    const { name , question , answer  } = content;
    return /*#__PURE__*/ React.createElement(FaqItem, {
        id: name,
        question: question,
        answer: answer.value
    });
});
const CmsFaq = ({ page , content: content1  })=>{
    const { name , title , faqItems  } = content1;
    var ref;
    const items = (ref = faqItems === null || faqItems === void 0 ? void 0 : faqItems.map((item)=>{
        const document = page === null || page === void 0 ? void 0 : page.getContent(item);
        const content = parseBloomreachContent(document === null || document === void 0 ? void 0 : document.getData());
        return swcHelpers.objectSpread({}, content, {
            document
        });
    })) !== null && ref !== void 0 ? ref : [];
    var ref1;
    const ids = items.map((item)=>{
        return (ref1 = item === null || item === void 0 ? void 0 : item.name) !== null && ref1 !== void 0 ? ref1 : '';
    });
    return /*#__PURE__*/ React.createElement(Faq$1, {
        name: name,
        title: title,
        ids: ids
    }, items.map((item, i)=>item && /*#__PURE__*/ React.createElement(CmsFaqItem, {
            key: `${i}-${item.id}`,
            content: item
        })
    ));
};
var Faq = compose(withBreakpointVisibility, withHidableContent, withBloomreachData, withAnchor)(CmsFaq);

const CmsFooter = ({ useAlternativeLogoInFooter =true  })=>{
    var ref, ref1;
    const { site  } = React.useContext(RuntimeContext);
    const { siteLogos  } = useBloomreach();
    const page = useBrPageContext();
    const component = useBrComponentContext();
    const newsletterData = useBloomreachNewsletterData();
    const preview = page === null || page === void 0 ? void 0 : page.isPreview();
    var ref2;
    const siteName = (ref2 = site === null || site === void 0 ? void 0 : site.name) !== null && ref2 !== void 0 ? ref2 : 'Unknown';
    const fallbackLogo = useAlternativeLogoInFooter ? siteLogos.alternative : siteLogos.main;
    const logo = {
        url: siteLogos.logoFooter || fallbackLogo,
        altText: siteName
    };
    if (!page || !component) {
        return null;
    }
    // Footer content
    const { document: documentRef  } = component.getModels();
    const document = page.getContent(documentRef);
    const content = document === null || document === void 0 ? void 0 : document.getData();
    // Footer bottom menu
    const menuDocument = ((ref = component.getComponent('menu')) === null || ref === void 0 ? void 0 : ref.getModels()) || {};
    const menuItems = createMenu(menuDocument, page);
    const newsletterProps = swcHelpers.objectSpread({}, newsletterData, {
        modalButtonTitle: content === null || content === void 0 ? void 0 : (ref1 = content.newsletterModal) === null || ref1 === void 0 ? void 0 : ref1.modalButtonTitle
    });
    return /*#__PURE__*/ React.createElement(React.Fragment, null, preview && document && /*#__PURE__*/ React.createElement("div", {
        style: {
            position: 'relative'
        }
    }, /*#__PURE__*/ React.createElement(BrManageContentButton, {
        content: document
    })), content && /*#__PURE__*/ React.createElement(Footer, swcHelpers.extends({}, content, {
        logo: logo,
        newsletterProps: newsletterProps,
        siteName: siteName
    }), (menuItems === null || menuItems === void 0 ? void 0 : menuItems.length) > 0 && /*#__PURE__*/ React.createElement(React.Fragment, null, preview && menuDocument.menu && /*#__PURE__*/ React.createElement("div", {
        style: {
            position: 'relative'
        }
    }, /*#__PURE__*/ React.createElement(BrManageMenuButton, {
        menu: menuDocument.menu
    })), /*#__PURE__*/ React.createElement(FooterLinks, {
        items: menuItems
    }))));
};

const CmsHeader$1 = ({ headerLogo , headerLogoMobile , headerLogoLink , headerMenuItems , navMenuItems , appointmentMenuItems , headerMenuModel , navMenuModel , appointmentMenuModel , pageRoutes , availableLocales , countrySwitcherOptions , myAccountButton , preview , renderCartButton , onSubmitSearch , formatProductUrl , staticsUrl , algoliaHeaderConfig , renderSiteNav =true , hasOnlyLogo =false , siteName , ordersUrl ,  })=>/*#__PURE__*/ React.createElement(React.Fragment, null, preview && headerMenuModel && /*#__PURE__*/ React.createElement("div", {
        style: {
            position: 'relative'
        }
    }, /*#__PURE__*/ React.createElement(BrManageMenuButton, {
        menu: headerMenuModel
    })), /*#__PURE__*/ React.createElement(Header, {
        logo: headerLogo,
        logoMobile: headerLogoMobile,
        logoLink: headerLogoLink,
        pageRoutes: pageRoutes,
        menuItems: headerMenuItems,
        myAccountButton: myAccountButton,
        renderCartButton: renderCartButton,
        availableLocales: availableLocales,
        staticsUrl: staticsUrl,
        algoliaHeaderConfig: algoliaHeaderConfig,
        onSubmit: onSubmitSearch,
        preview: preview,
        countrySwitcherOptions: countrySwitcherOptions,
        renderSiteNav: renderSiteNav,
        hasOnlyLogo: hasOnlyLogo,
        siteName: siteName,
        formatProductUrl: formatProductUrl,
        ordersUrl: ordersUrl
    }, renderSiteNav && /*#__PURE__*/ React.createElement(React.Fragment, null, preview && navMenuModel && /*#__PURE__*/ React.createElement("div", {
        style: {
            position: 'relative'
        }
    }, /*#__PURE__*/ React.createElement(BrManageMenuButton, {
        menu: navMenuModel
    })), /*#__PURE__*/ React.createElement(SiteNav, {
        menuItems: navMenuItems,
        appointmentItems: appointmentMenuItems,
        routes: {
            cart: pageRoutes.cart,
            stores: pageRoutes.stores
        },
        countrySwitcherOptions: countrySwitcherOptions,
        renderCartButton: renderCartButton,
        ordersUrl: ordersUrl
    }), preview && appointmentMenuModel && /*#__PURE__*/ React.createElement("div", {
        style: {
            position: 'relative'
        }
    }, /*#__PURE__*/ React.createElement(BrManageMenuButton, {
        menu: appointmentMenuModel
    })))))
;

function isMoreSpecific(nextLink = '', prevLink = '') {
    const isEqual = (nextLink.match(/\//g) || []).length === (prevLink.match(/\//g) || []).length;
    if (isEqual) {
        return nextLink.length > prevLink.length;
    } else {
        return (nextLink.match(/\//g) || []).length > (prevLink.match(/\//g) || []).length;
    }
}
function checkIfActive(currentPath = '', itemPath = '') {
    // root c.q. home
    if (currentPath === '/' && itemPath === '/') {
        return true;
    } else {
        return currentPath.startsWith(itemPath);
    }
}
function convertToBloomreachMenuItem(items) {
    const getHref = (item)=>item.link ? item.link.href : ''
    ;
    return items.map((item)=>{
        var ref;
        return swcHelpers.objectSpread({}, item, {
            name: item.label,
            depth: 1,
            expanded: false,
            selected: false,
            repositoryBased: false,
            _links: {
                site: {
                    href: getHref(item),
                    type: 'external',
                    active: (ref = item.link) === null || ref === void 0 ? void 0 : ref.active
                }
            },
            parameters: swcHelpers.objectSpread({}, item.parameters),
            childMenuItems: (item.childMenuItems || []).map((childItem)=>swcHelpers.objectSpread({}, childItem, {
                    name: childItem.label,
                    depth: 1,
                    expanded: false,
                    selected: false,
                    repositoryBased: false,
                    parameters: swcHelpers.objectSpread({}, childItem.parameters),
                    childMenuItems: [],
                    _links: {
                        site: {
                            href: getHref(childItem),
                            type: 'external'
                        }
                    }
                })
            )
        });
    });
}
const createMyAccountMobileMenuStructure = (intl, formattedRoutes, logout, isLoggedIn, isAddressBookEnabled, isAABEnabled, isSubscriptionsV2Enabled)=>{
    const submenu = [
        {
            name: '',
            depth: 1,
            expanded: false,
            repositoryBased: false,
            selected: false,
            _links: {},
            parameters: {},
            childMenuItems: formattedRoutes ? convertToBloomreachMenuItem(getMyAccountMenuItems(logout, formattedRoutes, intl, isLoggedIn, isAddressBookEnabled, isAABEnabled, isSubscriptionsV2Enabled)) : []
        }, 
    ];
    return {
        name: intl.getLabel('account.menu.myAccount'),
        depth: 0,
        expanded: false,
        repositoryBased: false,
        selected: false,
        _links: {},
        parameters: {
            menuKey: 'my-account'
        },
        childMenuItems: submenu
    };
};
const createLocaleSwitcherMenuStructure = (availableLocales, intl)=>{
    const localeSwitcherMenuLabel = getLocaleSwitcherMenuLabel(intl.locale);
    const localeMenuItems = getLocaleSwitcherData(availableLocales, false);
    const activeLocale = availableLocales.find((item)=>item.locale.replace('_', '-') === intl.locale
    );
    return {
        label: localeSwitcherMenuLabel,
        parameters: {
            menuKey: 'locale-switcher'
        },
        childMenuItems: [
            {
                label: activeLocale ? activeLocale.chooseLanguage : localeSwitcherMenuLabel,
                childMenuItems: localeMenuItems
            }, 
        ]
    };
};
const getCountrySwitcherUrls = (content)=>{
    if (!(content === null || content === void 0 ? void 0 : content.imageLinkList)) {
        return undefined;
    }
    var ref, ref1, ref2;
    return content.imageLinkList.map((item)=>{
        var ref6, ref3, ref4, ref5;
        return {
            image: (ref = (ref6 = item.image) === null || ref6 === void 0 ? void 0 : ref6.url) !== null && ref !== void 0 ? ref : '',
            label: (ref1 = (ref3 = item.link) === null || ref3 === void 0 ? void 0 : ref3.label) !== null && ref1 !== void 0 ? ref1 : '',
            url: (ref2 = (ref4 = item.link) === null || ref4 === void 0 ? void 0 : ref4.url) !== null && ref2 !== void 0 ? ref2 : '',
            openInNewWindow: ((ref5 = item.link) === null || ref5 === void 0 ? void 0 : ref5.openInNewWindow) || false
        };
    });
};
const withMyAccountMenuItems = (navMenuData, intl, formattedRoutes, logout, isLoggedIn, isAddressBookEnabled, isAABEnabled, isSubscriptionsV2Enabled)=>{
    var ref;
    const myAccountMenuItems = createMyAccountMobileMenuStructure(intl, formattedRoutes, logout, isLoggedIn, isAddressBookEnabled, isAABEnabled, isSubscriptionsV2Enabled);
    return swcHelpers.objectSpread({}, navMenuData, {
        menu: swcHelpers.objectSpread({}, navMenuData === null || navMenuData === void 0 ? void 0 : navMenuData.menu, {
            name: 'my-account-menu',
            selectSiteMenuItem: null,
            siteMenuItems: [
                ...(navMenuData === null || navMenuData === void 0 ? void 0 : (ref = navMenuData.menu) === null || ref === void 0 ? void 0 : ref.siteMenuItems) || [],
                myAccountMenuItems
            ]
        })
    });
};
const CmsHeader = (props)=>{
    var ref13, ref7, ref8, ref9;
    const intl = useIntl();
    const page = useBrPageContext();
    const component = useBrComponentContext();
    const { isAddressBookEnabled , isMultilingualEnabled , isMyAccountEnabled , isSubscriptionsV2Enabled , isAABEnabled ,  } = useFeatureToggles();
    const { algolia , grandvision , site , bloomreach  } = React.useContext(RuntimeContext);
    const { siteLogos , preview , path  } = useBloomreach();
    const [_, { formatProductUrl  }] = useBloomreachProductRoutes();
    const { routes: pageRoutes  } = useBloomreachPageRoutes();
    var ref10;
    const siteName = (ref10 = site === null || site === void 0 ? void 0 : site.name) !== null && ref10 !== void 0 ? ref10 : 'Unknown';
    var ref11;
    const logoLink = (ref11 = getUrl(pageRoutes.root, page)) !== null && ref11 !== void 0 ? ref11 : '/';
    // My account actions
    const dispatch = useDispatch();
    const logout = React.useCallback(()=>dispatch(Actions.LogoutActions.logOut())
    , [
        dispatch
    ]);
    const recoverSession = React.useCallback(()=>dispatch(Actions.LoginActions.recoverSession())
    , [
        dispatch
    ]);
    // My account state
    const auth = useSelector(Selectors.getAuth);
    const formattedRoutes = useSelector(Selectors.getFormattedRoutes);
    /**
   * State
   */ const [mounted, setMounted] = React.useState(false);
    var ref12;
    /**
   * Create all menu data with BrPageModel
   */ const { routes ={} , localeOptions , countrySwitcher  } = (ref12 = (ref13 = page === null || page === void 0 ? void 0 : page.getComponent()) === null || ref13 === void 0 ? void 0 : ref13.getModels()) !== null && ref12 !== void 0 ? ref12 : {};
    const headerMenu = (ref7 = component === null || component === void 0 ? void 0 : component.getComponent('top-menu')) === null || ref7 === void 0 ? void 0 : ref7.getModels();
    const navMenu = (ref8 = component === null || component === void 0 ? void 0 : component.getComponent('main-menu')) === null || ref8 === void 0 ? void 0 : ref8.getModels();
    const appointmentMenu = (ref9 = component === null || component === void 0 ? void 0 : component.getComponent('appointment-menu')) === null || ref9 === void 0 ? void 0 : ref9.getModels();
    // Country switcher - this is not the language/locale switcher
    const countrySwitcherContent = countrySwitcher && (page === null || page === void 0 ? void 0 : page.getContent(countrySwitcher));
    const countrySwitcherOptions = getCountrySwitcherUrls(countrySwitcherContent === null || countrySwitcherContent === void 0 ? void 0 : countrySwitcherContent.getData());
    const headerMenuItems = createMenu(headerMenu, page);
    const appointmentMenuItems = createMenu(appointmentMenu, page);
    /**
   * Using refs so these static objects are not re-created on each render
   */ const headerLogo = React.useRef({
        url: siteLogos.main,
        altText: siteName
    });
    const headerLogoMobile = React.useRef({
        url: siteLogos.mobile,
        altText: siteName
    });
    /**
   * My account menu setup
   */ const authRef = React.useRef(auth);
    const isUserLoggedIn = State.getLoggedInState(auth) === 'logged-in';
    const myAccountButton = React.useMemo(()=>{
        if (!isMyAccountEnabled || !formattedRoutes) {
            return null;
        }
        const menuItems = getMyAccountMenuItems(logout, formattedRoutes, intl, isUserLoggedIn, isAddressBookEnabled, isAABEnabled, isSubscriptionsV2Enabled);
        const items = convertToBloomreachMenuItem(menuItems);
        return /*#__PURE__*/ React.createElement(MyAccountMenu, {
            menuItems: mapToMenu(items, page)
        });
    }, [
        isMyAccountEnabled,
        formattedRoutes,
        logout,
        intl,
        isUserLoggedIn,
        isAddressBookEnabled,
        isAABEnabled,
        isSubscriptionsV2Enabled,
        page, 
    ]);
    const ordersUrl = React.useMemo(// ordersUrl should be defined when my-account is enabled and the orders route exists
    ()=>{
        return isMyAccountEnabled ? formattedRoutes === null || formattedRoutes === void 0 ? void 0 : formattedRoutes.orders : undefined;
    }, [
        formattedRoutes === null || formattedRoutes === void 0 ? void 0 : formattedRoutes.orders,
        isMyAccountEnabled
    ]);
    React.useEffect(()=>{
        // Runs on mount and updates
        if ((!mounted || State.isUninitialized(authRef.current)) && State.isInitialized(auth)) {
            recoverSession();
        }
        setMounted(true);
        authRef.current = auth;
        // Let analytics know if user is logged in
        setUserObject(isUserLoggedIn);
    }, [
        mounted,
        auth,
        recoverSession,
        isUserLoggedIn
    ]);
    /**
   * Navigation menu items with my account menu
   */ const navMenuItems = React.useMemo(()=>{
        const data = isMyAccountEnabled ? withMyAccountMenuItems(navMenu, intl, formattedRoutes, logout, isUserLoggedIn, isAddressBookEnabled, isAABEnabled, isSubscriptionsV2Enabled) : navMenu;
        const menuItems = createMenu(data, page);
        const result = isMultilingualEnabled ? [
            ...menuItems,
            createLocaleSwitcherMenuStructure(localeOptions || [], intl)
        ] : menuItems;
        const currentPath = path === null || path === void 0 ? void 0 : path.split('?')[0];
        // TODO: this should be changed when the upgrade to BR 14.5 is done
        const previewLinkPrefix = `/site/_cmsinternal/${bloomreach === null || bloomreach === void 0 ? void 0 : bloomreach.channel}`;
        // Check if a sitenav item's url corresponds with the current url path
        const activeItems = result.filter((menuItem)=>{
            var ref;
            return checkIfActive(currentPath, (((ref = menuItem.link) === null || ref === void 0 ? void 0 : ref.href) || '').replace(previewLinkPrefix, ''));
        });
        const active = activeItems.sort((a, b)=>{
            var ref, ref14;
            return isMoreSpecific((ref = a.link) === null || ref === void 0 ? void 0 : ref.href, (ref14 = b.link) === null || ref14 === void 0 ? void 0 : ref14.href) ? -1 : 1;
        }).shift();
        if (active === null || active === void 0 ? void 0 : active.link) {
            active.link = swcHelpers.objectSpread({}, active.link, {
                active: true
            });
        }
        return result;
    }, [
        isMyAccountEnabled,
        isSubscriptionsV2Enabled,
        navMenu,
        intl,
        formattedRoutes,
        logout,
        isUserLoggedIn,
        isAddressBookEnabled,
        isAABEnabled,
        page,
        isMultilingualEnabled,
        localeOptions,
        path,
        bloomreach === null || bloomreach === void 0 ? void 0 : bloomreach.channel, 
    ]);
    /**
   * Search callback
   */ const handleSearch = React.useCallback((query, type)=>{
        if (!algolia) {
            return;
        }
        const { indexes , productIndex  } = algolia;
        const storeIndex = indexes === null || indexes === void 0 ? void 0 : indexes.find((index)=>index.type === 'stores'
        );
        const contentIndex = indexes === null || indexes === void 0 ? void 0 : indexes.find((index)=>index.type === 'content'
        );
        switch(type){
            case 'products':
                window.location.assign(`${productIndex.targetPath}${encodeURIComponent(query)}`);
                break;
            case 'stores':
                storeIndex && window.location.assign(`${storeIndex.targetPath}${encodeURIComponent(query)}`);
                break;
            case 'content':
                contentIndex && window.location.assign(`${contentIndex.targetPath}${encodeURIComponent(query)}#pages`);
                break;
            default:
                console.warn(`Trying to perform search action for unknown Algolia index type: ${type}`);
        }
    }, [
        algolia
    ]);
    if ((!headerMenu || !headerMenuItems) && (!navMenu || !navMenuItems)) {
        console.error('No configuration or components found for Header in CMS model!');
        return null;
    }
    return /*#__PURE__*/ React.createElement(CmsHeader$1, swcHelpers.extends({}, props, {
        headerMenuModel: headerMenu === null || headerMenu === void 0 ? void 0 : headerMenu.menu,
        headerMenuItems: headerMenuItems,
        headerLogoLink: logoLink,
        navMenuModel: navMenu === null || navMenu === void 0 ? void 0 : navMenu.menu,
        navMenuItems: navMenuItems,
        appointmentMenuModel: appointmentMenu === null || appointmentMenu === void 0 ? void 0 : appointmentMenu.menu,
        appointmentMenuItems: appointmentMenuItems,
        myAccountButton: myAccountButton,
        onSubmitSearch: handleSearch,
        preview: preview,
        availableLocales: localeOptions,
        countrySwitcherOptions: countrySwitcherOptions,
        headerLogo: headerLogo.current,
        headerLogoMobile: headerLogoMobile.current,
        pageRoutes: routes,
        formatProductUrl: formatProductUrl,
        staticsUrl: (grandvision === null || grandvision === void 0 ? void 0 : grandvision.staticsUrl) || '',
        algoliaHeaderConfig: algolia,
        ordersUrl: ordersUrl
    }));
};

function rewriteCanonical(canonicalUrl, selfUrl, pageNr) {
    // Strip scheme and host from url and save just the pathname parts
    const [, , , ...path] = canonicalUrl.split('/');
    const pageParameter = pageNr && pageNr !== 1 ? `?page=${pageNr}` : '';
    return `${selfUrl}/${path.join('/')}${pageParameter}`;
}
function createHreflangLink(hreflang, selfUrl) {
    const absoluteUrlPattern = /^https?:\/\/|^\/\//i;
    const url = absoluteUrlPattern.test(hreflang.url) ? hreflang.url : `${selfUrl || ''}${hreflang.url}`;
    const locale = hreflang.locale.replace('_', '-').toLowerCase();
    return {
        url,
        locale
    };
}
const CmsHeadTags = ({ title , description , ogUrl , ogImage , hreflangs , noIndex , noFollow , pageNr , children ,  })=>/*#__PURE__*/ React.createElement(RuntimeContext.Consumer, null, ({ headComponent , selfUrl , site ={}  })=>{
        const siteName = site.name || '';
        const props = {
            title: title ? `${title} | ${siteName}` : siteName,
            description: description || site.description || '',
            ogImage: ogImage || site.ogImage || '',
            ogUrl: rewriteCanonical(ogUrl || site.url || '', selfUrl, pageNr),
            hreflangs: Array.isArray(hreflangs) && hreflangs.length > 1 ? hreflangs.map((entry)=>createHreflangLink(entry, selfUrl)
            ) : [],
            headComponent,
            noIndex,
            noFollow,
            children
        };
        return /*#__PURE__*/ React.createElement(HeadTags, swcHelpers.extends({}, props));
    })
;

const variantMap = {
    h1: 'large',
    h2: 'medium',
    h3: 'small',
    h4: 'smallest',
    h5: 'subheading1',
    h6: 'subheading2'
};
const CmsHeading = ({ meta: { title , type , alignment  }  })=>/*#__PURE__*/ React.createElement(Heading$1, {
        className: `heading heading--${alignment}`,
        variant: variantMap[type],
        align: alignment
    }, title)
;
var Heading = compose(withBreakpointVisibility, withHidableContent, withBloomreachData, withAnchor)(CmsHeading);

const CmsIconList = ({ page , content , theme  })=>{
    const { iconLinkCompoundList =[] , title  } = content;
    const items = React.useMemo(()=>{
        return iconLinkCompoundList.map(({ icon , link  })=>({
                icon,
                url: getUrl(link.url || '', page),
                label: link.label
            })
        );
    }, [
        iconLinkCompoundList,
        page
    ]);
    return /*#__PURE__*/ React.createElement(IconList$1, {
        items: items,
        title: title,
        theme: theme
    });
};
var IconList = compose(withBreakpointVisibility, withHidableContent, withBloomreachData, withEditableContent, withAnchor)(CmsIconList);

const CmsNewsletter = ({ component , meta  })=>{
    const newsletterData = useBloomreachNewsletterData();
    const { newsletterImage  } = component.getModels();
    const image = parseBloomreachImage(newsletterImage);
    const alignment = meta.template.includes('right') ? 'right' : 'left';
    return /*#__PURE__*/ React.createElement(Newsletter$1, swcHelpers.extends({}, newsletterData, {
        alignment: alignment,
        image: image === null || image === void 0 ? void 0 : image.original,
        mobileImage: image === null || image === void 0 ? void 0 : image.mobile,
        variant: "bloomreach-component"
    }));
};
var Newsletter = compose(withHidableContent, withBloomreachData, withEditableContent)(CmsNewsletter);

const CmsNotificationBar = ({ content , theme ='dark'  })=>{
    const { title , description , links  } = content;
    const [link] = useBloomreachLinks(links);
    if (!title && !description) {
        return null;
    }
    var ref;
    return /*#__PURE__*/ React.createElement(NotificationBar$1, {
        title: title,
        description: description,
        theme: theme,
        link: swcHelpers.objectSpread({}, link, {
            label: (ref = link === null || link === void 0 ? void 0 : link.label) !== null && ref !== void 0 ? ref : ''
        })
    });
};
var NotificationBar = compose(withBreakpointVisibility, withHidableContent, withBloomreachData, withEditableContent, withAnchor)(CmsNotificationBar);

const CmsOlapic = ({ meta  })=>{
    const { locale  } = useIntl();
    const { olapic: { apiKey , url  } ,  } = React__default.useContext(RuntimeContext);
    return /*#__PURE__*/ React__default.createElement(Olapic$1, {
        apiKey: apiKey,
        instance: meta.instance,
        locale: locale.replace('_', '-'),
        olapic: meta.olapic,
        src: url,
        tags: meta.tags
    });
};
var Olapic = compose(withBreakpointVisibility, withHidableContent, withBloomreachData, withAnchor)(CmsOlapic);

const CmsPolicyBar = ({ content , theme ='default'  })=>{
    var ref;
    if (!(content === null || content === void 0 ? void 0 : (ref = content.policyItems) === null || ref === void 0 ? void 0 : ref.length)) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(UspGroup, {
        items: content.policyItems,
        theme: theme
    });
};
var PolicyBar = compose(withBreakpointVisibility, withHidableContent, withBloomreachData, withEditableContent, withAnchor)(CmsPolicyBar);

function createProductBreadcrumb(categoryBreadcrumb, product, productType, sku, locale) {
    const activeVariant = Helpers.getActiveVariant(product, sku);
    const productName = product && getLocalizedValue(product.name, locale);
    if (productType && (CommerceToolsApi.isLenses(productType) || CommerceToolsApi.isContactLensBundle(productType))) {
        return productName && sku && categoryBreadcrumb ? {
            name: productName
        } : null;
    }
    if (productType && CommerceToolsApi.isSolutions(productType)) {
        const packageOption = activeVariant && getAttrValue(activeVariant, 'packageOption');
        return productName && packageOption && sku && categoryBreadcrumb ? {
            name: `${productName} ${packageOption}`
        } : null;
    }
    return activeVariant.name && sku && categoryBreadcrumb ? {
        name: activeVariant.name
    } : null;
}

function getCategoryRoute(pageModel, productType = '') {
    var _models;
    const { productRoutes ={}  } = (_models = pageModel === null || pageModel === void 0 ? void 0 : pageModel.page.models) !== null && _models !== void 0 ? _models : {};
    const key1 = Object.keys(productRoutes).find((key)=>key === productType && productRoutes
    );
    return key1 && productRoutes[key1];
}

function createSubCategoryBreadcrumb(categoryRoute, product, productType, locale, page) {
    var ref;
    var ref1;
    // Get the path of the route (in BR preview you also get some querystring that we don't need to create the subcategory link)
    const categoryRoutePath = (ref1 = (ref = categoryRoute === null || categoryRoute === void 0 ? void 0 : categoryRoute.split('?')) === null || ref === void 0 ? void 0 : ref[0]) !== null && ref1 !== void 0 ? ref1 : '';
    // If the productType is of lenses we will use the replacementFrequency to create the url
    if (productType && CommerceToolsApi.isLenses(productType)) {
        const replacementFrequency = getLocalizedAttrValue(product.masterVariant, 'replacementFrequency', locale) || '';
        return replacementFrequency != '' ? {
            name: replacementFrequency,
            url: getUrl(`${categoryRoutePath}/${encodeURIComponent(sanitizeStringForUrl(replacementFrequency))}`, page)
        } : null;
    }
    // compose url for the accessories
    if (productType && CommerceToolsApi.isAccessories(productType)) {
        const accessoryType = getLocalizedAttrValue(product.masterVariant, 'accessoryType', locale) || '';
        return accessoryType !== '' ? {
            name: accessoryType,
            url: `${categoryRoutePath}/${encodeURIComponent(sanitizeStringForUrl(accessoryType))}`
        } : null;
    }
    // If the productType is solutions we will use the contactLensCareType to create the url
    if (productType && CommerceToolsApi.isSolutions(productType)) {
        const contactLensCareType = product && getLocalizedAttrValue(product.masterVariant, 'contactLensCareType', locale) || '';
        return contactLensCareType ? {
            name: contactLensCareType,
            url: `${categoryRoutePath}/${encodeURIComponent(sanitizeStringForUrl(contactLensCareType))}`
        } : null;
    }
    const brand = getAttrValue(product.masterVariant, 'brand');
    return isString(brand) && brand !== '' ? {
        name: String(brand),
        url: getUrl(`${categoryRoutePath}/${encodeURIComponent(sanitizeStringForUrl(brand))}`, page)
    } : null;
}

/** Returns the main-menu data from the page model */ function getMainMenuItems(pageModel) {
    var ref;
    const menu = getComponentByPath('header/main-menu', pageModel);
    var _siteMenuItems;
    return (_siteMenuItems = menu === null || menu === void 0 ? void 0 : (ref = menu.models) === null || ref === void 0 ? void 0 : ref.menu.siteMenuItems) !== null && _siteMenuItems !== void 0 ? _siteMenuItems : [];
}
/** Returns the name that belongs to a given route from the main menu */ function getNameByProductRoute(categoryRoute, pageModel) {
    const menu = getMainMenuItems(pageModel);
    const route = findRouteByUrl(categoryRoute, menu);
    return route && route.name;
}
/**  Returns the route for a given url and an array of menu items */ function findRouteByUrl(categoryRoute, routes) {
    for (const route of routes){
        var ref, ref1;
        if (((ref = route._links.site) === null || ref === void 0 ? void 0 : ref.href) && ((ref1 = route._links.site) === null || ref1 === void 0 ? void 0 : ref1.href) === categoryRoute) {
            return route;
        }
        // Check the childMenuItems for the route
        if (route.childMenuItems.length) {
            const result = findRouteByUrl(categoryRoute, route.childMenuItems);
            if (result) return result;
        }
    }
}
function createCategoryBreadcrumb(pageModel, categoryRoute, page) {
    if (!pageModel || !categoryRoute) {
        return null;
    }
    const name = getNameByProductRoute(categoryRoute, pageModel);
    const url = getUrl(categoryRoute, page);
    return name ? {
        name,
        url
    } : null;
}

const CmsProductBreadcrumbs = ({ product , sku  })=>{
    var ref;
    const { locale  } = useIntl();
    const { pageModel  } = useBloomreach();
    const page = useBrPageContext();
    const productType = (ref = product.productType.obj) === null || ref === void 0 ? void 0 : ref.name;
    const categoryRoute = getCategoryRoute(pageModel, productType);
    if (!categoryRoute) {
        console.warn('Could not find a product category route to render PDP breadcrumbs');
        return null;
    }
    const categoryBreadcrumb = createCategoryBreadcrumb(pageModel, categoryRoute, page);
    const subCategoryBreadcrumb = createSubCategoryBreadcrumb(categoryRoute, product, productType, locale, page);
    const productBreadcrumb = createProductBreadcrumb(categoryBreadcrumb, product, productType, sku, locale);
    const breadcrumbs = [
        categoryBreadcrumb,
        subCategoryBreadcrumb,
        productBreadcrumb
    ].filter(Boolean);
    return /*#__PURE__*/ React.createElement(Container, null, /*#__PURE__*/ React.createElement(Breadcrumbs, {
        pages: breadcrumbs
    }));
};

function createGraphProductBreadcrumb(categoryBreadcrumb, productName, brandName) {
    let name = productName;
    if (!!brandName) {
        // if the product name already includes the brand name, strip it out
        // the brandName is used in a separate breadcrumb and shouldn't show up twice
        name = productName.replace(brandName, '').trim();
    }
    return productName && categoryBreadcrumb ? {
        name
    } : null;
}

function createGraphSubCategoryBreadcrumb(categoryRoute, brand, page) {
    var ref;
    var ref1;
    // Get the path of the route (in BR preview you also get some querystring that we don't need to create the subcategory link)
    const categoryRoutePath = (ref1 = (ref = categoryRoute === null || categoryRoute === void 0 ? void 0 : categoryRoute.split('?')) === null || ref === void 0 ? void 0 : ref[0]) !== null && ref1 !== void 0 ? ref1 : '';
    return isString(brand) && brand !== '' ? {
        name: String(brand),
        url: getUrl(`${categoryRoutePath}/${encodeURIComponent(sanitizeStringForUrl(brand))}`, page)
    } : null;
}

const CmsGraphProductBreadcrumbs = ({ variant , productType , brandName ,  })=>{
    const { pageModel  } = useBloomreach();
    const page = useBrPageContext();
    const categoryRoute = getCategoryRoute(pageModel, productType);
    if (!categoryRoute) {
        console.warn('Could not find a product category route to render PDP breadcrumbs');
        return null;
    }
    const categoryBreadcrumb = createCategoryBreadcrumb(pageModel, categoryRoute, page);
    const subCategoryBreadcrumb = createGraphSubCategoryBreadcrumb(categoryRoute, brandName, page);
    const productBreadcrumb = createGraphProductBreadcrumb(categoryBreadcrumb, variant.displayName, brandName);
    const breadcrumbs = [
        categoryBreadcrumb,
        subCategoryBreadcrumb,
        productBreadcrumb
    ].filter(Boolean);
    return /*#__PURE__*/ React.createElement(Container, null, /*#__PURE__*/ React.createElement(Breadcrumbs, {
        pages: breadcrumbs
    }));
};

function createProductTypeBreadcrumb(pageRoutes, path = '', transformUrl) {
    const { name , url  } = findProductTypeUrl(pageRoutes, path);
    return {
        name: name || '',
        url: url && transformUrl ? transformUrl(url) : url
    };
}
function findProductTypeUrl(pageRoutes, path) {
    // Inverse pageRoutes so we can access it by path instead of name
    const routesByPath = Object.entries(pageRoutes).reduce((acc, [key, value])=>{
        acc[value] = key;
        return acc;
    }, {});
    // Retrieve combined path segments and check if it exists in the pageRoutes
    const routes = getRouteSegmentsByPath(path);
    const productTypePath = routes.find((route)=>route in routesByPath
    );
    return productTypePath ? {
        url: productTypePath,
        name: routesByPath[productTypePath]
    } : {};
}
/**
 * Returns all possible combined path segments from a given path
 * For a path `/contact-lenses/solutions/branding` we would like to have all possible combined sections:
 *    `/contact-lenses/solutions/branding`
 *    `/contact-lenses/solutions`
 *    `/contact-lenses`
 */ function getRouteSegmentsByPath(path) {
    return path.split('/').slice(1).reduce((prev, curr, index)=>{
        // append the current path section to the previous section
        const newPathSection = prev[index].concat(`/${curr}`);
        prev.push(newPathSection);
        return prev;
    }, [
        ''
    ]).slice(1).reverse();
}
const getProductTypeName = (breadcrumb, pageModel, getLabel)=>{
    var ref;
    const productTypeName = pageModel && breadcrumb.url && getNameByProductRoute(breadcrumb.url, pageModel);
    return productTypeName ? productTypeName : getLabel(`products.${(ref = breadcrumb.name) === null || ref === void 0 ? void 0 : ref.toLowerCase()}`, {
        defaultValue: breadcrumb.name
    });
};

const CmsProductCatalogBreadcrumbs = ({ title  })=>{
    const [, , pageRoutes] = useBloomreachProductRoutes();
    const { routes  } = useBloomreachPageRoutes();
    const { pageModel , path  } = useBloomreach();
    const page = useBrPageContext();
    const { getLabel  } = useIntl();
    const createUrl = (url)=>{
        return url && (page === null || page === void 0 ? void 0 : page.isPreview()) ? page.getUrl(url) : url;
    };
    // Remove parameters from the path
    const sanitizedPath = path === null || path === void 0 ? void 0 : path.split('?')[0];
    // Create the root route
    const rootBreadcrumb = {
        name: getLabel('account.menu.home', {
            defaultValue: 'Home'
        }),
        url: createUrl(routes.root)
    };
    const breadcrumbs = [
        rootBreadcrumb
    ];
    // Retrieve the product type breadcrumb
    const productTypeBreadcrumb = createProductTypeBreadcrumb(pageRoutes, sanitizedPath, createUrl);
    if (productTypeBreadcrumb.name) {
        // Get the localized productType name
        productTypeBreadcrumb.name = getProductTypeName(productTypeBreadcrumb, pageModel, getLabel);
        breadcrumbs.push(productTypeBreadcrumb);
    }
    // If the path is not equal to the path of the productType breadcrumb,
    // we are not on a standard productType page and will use the title as the last breadcrumb
    if (productTypeBreadcrumb.url !== sanitizedPath) {
        breadcrumbs.push({
            name: title
        });
    }
    return /*#__PURE__*/ React__default.createElement(ProductCatalogBreadcrumbs, {
        breadcrumbs: breadcrumbs
    });
};

function getPromotion({ page , ref  }) {
    const document = ref && page.getContent(ref);
    const content = document === null || document === void 0 ? void 0 : document.getData();
    if (!document) {
        return undefined;
    }
    return swcHelpers.objectSpread({}, content, {
        links: content === null || content === void 0 ? void 0 : content.links[0],
        manageContentButton: /*#__PURE__*/ React__default.createElement(BrManageContentButton, {
            content: document
        })
    });
}
const CmsProductCatalog = ({ page , content  })=>{
    var ref5;
    const { getLabel  } = useIntl();
    const { isEnabled  } = useAlgoliaConfig();
    const { grandvision  } = React__default.useContext(RuntimeContext);
    const [_, { formatProductUrl  }] = useBloomreachProductRoutes();
    const { siteLogos  } = useBloomreach();
    const { title , smallPromotion , bigPromotion  } = content;
    const productPageSelector = (ref5 = content.productPageSelector) === null || ref5 === void 0 ? void 0 : ref5.filter((item)=>item.url && item.label
    );
    if (!isEnabled) {
        console.error("You're trying to render the <CmsProductCatalog />, but the `AlgoliaConfig` is not configured properly.");
        return null;
    }
    const promotionSmall = getPromotion({
        page,
        ref: smallPromotion
    });
    const promotionBig = getPromotion({
        page,
        ref: bigPromotion
    });
    return /*#__PURE__*/ React__default.createElement(AlgoliaSearch, null, (stateResults)=>{
        var ref, ref1, ref2, ref3, ref4;
         return React__default.createElement(React__default.Fragment, null, /*#__PURE__*/ React__default.createElement(CmsProductCatalogBreadcrumbs, {
            title: title
        }), /*#__PURE__*/ React__default.createElement(SearchPageHeader, {
            pageTitle: title,
            hitTitle: `${stateResults === null || stateResults === void 0 ? void 0 : stateResults.searchResults.nbHits} ${getLabel('search.results.amount.title')}`,
            description: (ref = content.description) === null || ref === void 0 ? void 0 : ref.value,
            facetOrder: stateResults === null || stateResults === void 0 ? void 0 : (ref1 = stateResults.searchResults) === null || ref1 === void 0 ? void 0 : (ref2 = ref1.renderingContent) === null || ref2 === void 0 ? void 0 : (ref3 = ref2.facetOrdering) === null || ref3 === void 0 ? void 0 : (ref4 = ref3.facets) === null || ref4 === void 0 ? void 0 : ref4.order,
            productPageSelector: productPageSelector
        }), /*#__PURE__*/ React__default.createElement(ConnectedProductListing, {
            formatProductUrl: formatProductUrl,
            staticsUrl: (grandvision === null || grandvision === void 0 ? void 0 : grandvision.staticsUrl) || '',
            promotionSmall: promotionSmall,
            promotionBig: promotionBig,
            reimbursableLogos: {
                default: siteLogos.reimbursable,
                alternative: siteLogos.reimbursableAlternative
            }
        }), /*#__PURE__*/ React__default.createElement(ScrollUpButton, null));
    });
};
var ProductCatalog = compose(withBreakpointVisibility, withHidableContent, withBloomreachData, withEditableContent, withAnchor)(CmsProductCatalog);

const CmsPromotion = ({ content  })=>{
    const { products =[] , productItemCompoundAlgolia =[] , supertitle ='' , title ='' , queryString ,  } = content;
    const promotionProducts = productItemCompoundAlgolia.length ? productItemCompoundAlgolia : products;
    const { algolia , grandvision  } = React.useContext(RuntimeContext);
    const [_, { formatProductUrl  }] = useBloomreachProductRoutes();
    const brcontext = useBloomreach();
    // We have two promotion components: old one is using queryString, and filterQuery for Algolia promotion
    const query = productItemCompoundAlgolia.length ? productItemCompoundAlgolia[0].filterQuery : queryString;
    const skus = React.useMemo(()=>promotionProducts.map(({ sku  })=>sku
        ).filter((sku)=>!!sku
        )
    , [
        promotionProducts, 
    ]);
    const config = React.useMemo(()=>{
        return {
            apiKey: algolia === null || algolia === void 0 ? void 0 : algolia.apiKey,
            appId: algolia === null || algolia === void 0 ? void 0 : algolia.appId,
            indexName: algolia === null || algolia === void 0 ? void 0 : algolia.productIndex.name,
            facets: algolia === null || algolia === void 0 ? void 0 : algolia.productIndex.facets
        };
    }, [
        algolia
    ]);
    // Either sku's or a querystring needs to be specified if we want to search
    const canSearch = promotionProducts.length > 0 || !!query;
    if (!canSearch || !title && !supertitle || !isValidAlgoliaSearchConfig(config)) {
        console.error('Error rendering CMSPromotion, missing content or invalid search configuration');
        return null;
    }
    return /*#__PURE__*/ React.createElement(QueryAlgoliaProducts, {
        algoliaConfig: config,
        skus: skus,
        queryString: query
    }, (searchState)=>{
        var ref;
        return !searchState.hasError && /*#__PURE__*/ React.createElement(ProductPromotions, {
            products: searchState.result,
            supertitle: supertitle,
            title: title,
            formatProductUrl: formatProductUrl,
            isLoading: searchState.isLoading,
            renderLocation: (ref = brcontext.pageModel) === null || ref === void 0 ? void 0 : ref.page.name
        });
    });
};
var Promotion = compose(withLazyLoading, withBreakpointVisibility, withHidableContent, withBloomreachData, withEditableContent, withAnchor)(CmsPromotion);

const CmsRecentlyViewedProducts = ()=>{
    var ref, ref1;
    const publicRuntimeConfig = React.useContext(RuntimeContext);
    const [_, { formatProductUrl  }] = useBloomreachProductRoutes();
    const { siteLogos  } = useBloomreach();
    const brcontext = useBloomreach();
    return /*#__PURE__*/ React.createElement(RecentlyViewedProducts$1, {
        reimbursableLogos: {
            default: siteLogos.reimbursable,
            alternative: siteLogos.reimbursableAlternative
        },
        formatProductUrl: formatProductUrl,
        staticsUrl: (publicRuntimeConfig === null || publicRuntimeConfig === void 0 ? void 0 : (ref = publicRuntimeConfig.grandvision) === null || ref === void 0 ? void 0 : ref.staticsUrl) || '',
        renderLocation: (ref1 = brcontext.pageModel) === null || ref1 === void 0 ? void 0 : ref1.page.name
    });
};
var RecentlyViewedProducts = compose(withBreakpointVisibility, withAnchor)(CmsRecentlyViewedProducts);

/**
 * This variant of the Rich Text component is used on container (drag & drop) pages
 */ const CmsRichText = ({ page , content , meta , template  })=>{
    var ref;
    const richText = useBloomreachRichText((ref = content.content) === null || ref === void 0 ? void 0 : ref.value);
    if (!richText) {
        return null;
    }
    switch(template){
        case 'default':
            return /*#__PURE__*/ React.createElement(RichTextArea, {
                content: richText,
                textColor: meta.textColor,
                backgroundColor: meta.backgroundColor
            });
        // Full-width template to display content in full container width.
        case 'full-width':
            return /*#__PURE__*/ React.createElement(RichTextArea, {
                content: richText,
                textColor: meta.textColor,
                backgroundColor: meta.backgroundColor,
                fullWidth: true
            });
        // Modal template is only added to be able to retrieve the content from the pageModel.
        case 'modal':
            return page.isPreview() ? /*#__PURE__*/ React.createElement(RichTextArea, {
                content: richText
            }) : null;
        default:
            return /*#__PURE__*/ React.createElement(Heading$1, {
                variant: "subheading1"
            }, "RichText component of type ", template, " not defined in RichTextCollection");
    }
};
var RichText = compose(withBreakpointVisibility, withHidableContent, withBloomreachData, withEditableContent, withAnchor)(CmsRichText);

const CmsSeo = ()=>{
    var ref, ref1;
    const { preview , pageSeo  } = useBloomreach();
    const { algoliaInstantSearchProps  } = useAlgoliaConfig();
    if (!pageSeo && !preview) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(React.Fragment, null, preview && /*#__PURE__*/ React.createElement("div", {
        style: {
            border: '2px solid black',
            padding: 10
        }
    }, "SEO configuration"), pageSeo && /*#__PURE__*/ React.createElement(CmsHeadTags, swcHelpers.extends({}, pageSeo, {
        ogUrl: pageSeo.canonicalUrl,
        noFollow: pageSeo.nofollow,
        noIndex: pageSeo.noindex,
        ogImage: ((ref = pageSeo.image) === null || ref === void 0 ? void 0 : ref.url) || '',
        pageNr: (ref1 = algoliaInstantSearchProps.searchState.configure) === null || ref1 === void 0 ? void 0 : ref1.page
    })));
};

const CmsSpotlightedStores = ({ content  })=>{
    const { routes  } = useBloomreachPageRoutes();
    const columns = React.useMemo(()=>{
        if (!content) {
            console.warn('no columns defined for CmsSpotlightedStores component');
            return [];
        }
        return [
            fromSpotlightedStoresContentToListBlock('firstListBlock', content === null || content === void 0 ? void 0 : content.leftColumn),
            fromSpotlightedStoresContentToListBlock('secondListBlock', content === null || content === void 0 ? void 0 : content.middleColumn),
            fromSpotlightedStoresContentToListBlock('thirdListBlock', content === null || content === void 0 ? void 0 : content.rightColumn), 
        ]/* if a column is empty move the others closer */ .filter((column)=>{
            return column.links.length > 0 || !!(column === null || column === void 0 ? void 0 : column.title);
        });
    }, [
        content
    ]);
    if (columns.length === 0) {
        return /*#__PURE__*/ React.createElement("div", {
            className: "site-container"
        }, /*#__PURE__*/ React.createElement("h1", null, "No columns defined for CmsSpotlightedStores component"));
    }
    return /*#__PURE__*/ React.createElement(SpotlightedStores$1, {
        items: columns,
        storesUrl: routes['store-overview']
    });
};
var SpotlightedStores = compose(withBreakpointVisibility, withHidableContent, withBloomreachData, withEditableContent)(CmsSpotlightedStores);

const mapButtonStyle = (buttonStyle = '')=>{
    const [color, variant] = buttonStyle.split(' ');
    return {
        color,
        variant
    };
};
const CmsFixedCta = ({ component  })=>{
    const { buttonText , buttonStyling , newWindow , iconImage , url , description ,  } = component.getParameters();
    const { color , variant  } = mapButtonStyle(buttonStyling);
    return /*#__PURE__*/ React.createElement(FixedCta, {
        buttonText: buttonText,
        color: color === 'primary' ? 'primary' : 'secondary',
        variant: variant === 'outline' ? 'outline' : 'solid',
        newWindow: newWindow,
        icon: iconImage,
        url: url,
        description: description
    });
};
compose(withBreakpointVisibility, withBloomreachData, withEditableContent, withAnchor)(CmsFixedCta);

const defaultLayout = {
    header: true,
    footer: true,
    navigation: true,
    hasOnlyLogoInHeader: false
};
const CmsPageLayout = ({ children , layoutSettings ={} , renderCartButton =defaultRenderCartButton , useAlternativeLogoInFooter ,  })=>{
    const layout = swcHelpers.objectSpread({}, defaultLayout, layoutSettings);
    const header = layout.header ? /*#__PURE__*/ React.createElement(CmsComponentSlot, {
        path: "header"
    }, /*#__PURE__*/ React.createElement(CmsHeader, {
        renderSiteNav: !layout.hasOnlyLogoInHeader && layout.navigation,
        hasOnlyLogo: layout.hasOnlyLogoInHeader,
        renderCartButton: renderCartButton
    })) : null;
    // The pre-footer should only be rendered on pages that have a footer
    const preFooter = layout.footer ? /*#__PURE__*/ React.createElement(CmsComponentSlot, {
        path: "prefooter/shared-container"
    }) : null;
    const footer = layout.footer ? /*#__PURE__*/ React.createElement(CmsComponentSlot, {
        path: "footer"
    }, /*#__PURE__*/ React.createElement(CmsFooter, {
        useAlternativeLogoInFooter: useAlternativeLogoInFooter
    })) : null;
    const { contentBlock , color , variant  } = useGetCtaButtonComponent();
    // The post-footer-cta-button should only be rendered on pages that have the cta-button
    const fixedBottomBarContent = contentBlock ? /*#__PURE__*/ React.createElement(FixedCta, {
        buttonText: contentBlock.buttonText,
        color: color,
        variant: variant,
        newWindow: contentBlock.newWindow,
        icon: contentBlock.iconImage,
        url: contentBlock.url,
        description: contentBlock.description
    }) : null;
    return /*#__PURE__*/ React.createElement(PageLayout, {
        header: header,
        footer: footer,
        preFooter: preFooter,
        fixedBottomBarContent: fixedBottomBarContent,
        hasSiteNav: layout.navigation,
        hasOnlyLogoInHeader: layout.hasOnlyLogoInHeader
    }, children);
};
function defaultRenderCartButton(item) {
    return /*#__PURE__*/ React.createElement(CartButton, {
        key: item.label,
        href: item.link && item.link.href,
        label: item.label,
        className: "header-menu__icon"
    });
}

const CmsContentLayout = ({ content , pageName  })=>{
    const { contentBlocks , coverImage , summary , title , url  } = content;
    const image = parseBloomreachImage(coverImage);
    const isBrandPage = pageName === 'brand-detail';
    return /*#__PURE__*/ React.createElement("div", {
        className: classnames('content-layout', {
            'content-layout--brand': isBrandPage
        })
    }, /*#__PURE__*/ React.createElement(CmsContentBreadcrumbs, {
        url: url
    }), image && /*#__PURE__*/ React.createElement(Banner, {
        useContainer: true,
        image: image.original,
        mobileImage: image.mobile
    }), title && /*#__PURE__*/ React.createElement(Heading$1, {
        variant: "large",
        className: "content-layout__title",
        align: isBrandPage ? 'center' : 'left'
    }, title), summary && /*#__PURE__*/ React.createElement("div", {
        className: "content-layout__summary rich-text-area"
    }, /*#__PURE__*/ React.createElement(Text, {
        align: isBrandPage ? 'center' : 'left'
    }, summary)), /*#__PURE__*/ React.createElement("article", {
        className: "content",
        id: "content-start"
    }, contentBlocks.length > 0 && /*#__PURE__*/ React.createElement(CmsContentBlocks, {
        contentBlocks: contentBlocks
    })));
};
var CmsContentLayout$1 = withEditableContent(CmsContentLayout);

const CmsBlogLayout = ({ content  })=>{
    const { getLabel , locale  } = useIntl();
    const { title , summary , coverImage , publishDate , readingTime , contentBlocks  } = content;
    const image = parseBloomreachImage(coverImage);
    return /*#__PURE__*/ React.createElement("div", {
        className: "blog-layout"
    }, image && /*#__PURE__*/ React.createElement(BlogDetailHeader, {
        title: title,
        image: image.original,
        mobileImage: image.mobile,
        scrollTo: "blog-content-start"
    }), summary && /*#__PURE__*/ React.createElement(BlogIntro, {
        locale: locale,
        summary: summary,
        publishDate: publishDate,
        readingTime: readingTime,
        minutesLabel: getLabel('blog.minutesToRead')
    }), /*#__PURE__*/ React.createElement("article", {
        className: "blog-content",
        id: "blog-content-start"
    }, contentBlocks.length > 0 && /*#__PURE__*/ React.createElement(CmsContentBlocks, {
        contentBlocks: contentBlocks
    })));
};
var CmsBlogLayout$1 = withEditableContent(CmsBlogLayout);

const isBlogArticle = (content)=>content.type === 'blogArticle'
;
const ContentPageType = ()=>{
    const { pageSeo  } = useBloomreach();
    const page = useBrPageContext();
    const component = useBrComponentContext();
    var ref;
    const { document: documentRef  } = (ref = component === null || component === void 0 ? void 0 : component.getModels()) !== null && ref !== void 0 ? ref : {};
    var ref1;
    const pageName = (ref1 = page === null || page === void 0 ? void 0 : page.getComponent().getName()) !== null && ref1 !== void 0 ? ref1 : '';
    const document = page === null || page === void 0 ? void 0 : page.getContent(documentRef);
    const content = document === null || document === void 0 ? void 0 : document.getData();
    if (!content) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(React.Fragment, null, document && /*#__PURE__*/ React.createElement(BrManageContentButton, {
        content: document
    }), isBlogArticle(content) ? /*#__PURE__*/ React.createElement(CmsBlogLayout$1, {
        content: content
    }) : /*#__PURE__*/ React.createElement(CmsContentLayout$1, {
        content: content,
        pageName: pageName
    }), pageSeo && content && /*#__PURE__*/ React.createElement(WebPage, {
        pageType: "Information",
        url: pageSeo.canonicalUrl,
        name: content.title,
        description: content.summary
    }));
};
const CmsContentPage = (props)=>{
    const { componentDefinitions ={} , pageModel , sdkConfig , pageSeo  } = useBloomreach();
    if (!sdkConfig) {
        console.warn(`CmsContentPage: can't render without a valid Bloomreach SDK config. Got: ${sdkConfig}`);
        return null;
    }
    if (!pageModel) {
        console.warn(`Tried to render CmsContainerPage without a valid Bloomreach PageModel: ${String(pageModel)}`);
        return null;
    }
    return /*#__PURE__*/ React.createElement(BrPage, {
        configuration: sdkConfig,
        mapping: componentDefinitions,
        page: pageModel
    }, pageSeo && /*#__PURE__*/ React.createElement(CmsSeo, null), /*#__PURE__*/ React.createElement(CmsPageLayout, swcHelpers.extends({}, props), /*#__PURE__*/ React.createElement(CmsComponentSlot, {
        path: "main/content"
    }, /*#__PURE__*/ React.createElement(ContentPageType, null))));
};

const CmsContainerPage = ({ children  })=>{
    const { componentDefinitions ={} , pageModel , sdkConfig , pageSeo  } = useBloomreach();
    const { getLabel  } = useIntl();
    React.useEffect(()=>{
        const pageName = pageModel === null || pageModel === void 0 ? void 0 : pageModel.page.name;
        const seoPageType = pageSeo === null || pageSeo === void 0 ? void 0 : pageSeo.type;
        DataLayer.pushPageTypeToDataLayer(seoPageType, pageName, getLabel);
    }, []);
    if (!sdkConfig) {
        console.warn(`Tried to render CmsContainerPage without a valid Bloomreach SDK configuration: ${String(sdkConfig)}`);
        return null;
    }
    if (!pageModel) {
        console.warn(`Tried to render CmsContainerPage without a valid Bloomreach PageModel: ${String(pageModel)}`);
        return null;
    }
    return /*#__PURE__*/ React.createElement(BrPage, {
        configuration: sdkConfig,
        mapping: componentDefinitions,
        page: pageModel
    }, children, pageSeo && /*#__PURE__*/ React.createElement(WebPage, {
        pageType: pageSeo.type,
        url: pageSeo.canonicalUrl,
        name: pageSeo.title,
        description: pageSeo.description
    }));
};

const defaultComponentDefinitions = {
    Accordion: CmsAccordion$1,
    Article: Article,
    Banner: CmsBanner$1,
    'Banner collection': CmsBannerCollection$1,
    'Blog Collection': BlogCollection,
    'Blog Overview': BlogOverview,
    'Brand collection': BrandCollection,
    'Brand List': BrandList,
    Carousel: CmsCarousel$1,
    Document: Document,
    FAQ: Faq,
    Heading: Heading,
    'Icon-Link List': IconList,
    IFrame: CmsIframe$1,
    Newsletter: Newsletter,
    'Notification bar': NotificationBar,
    'Olapic Widget': Olapic,
    Page: CmsEmpty,
    'Policy bar': PolicyBar,
    'Product catalog v2': ProductCatalog,
    'Promotion Algolia': Promotion,
    Promotion: Promotion,
    'Rich Text': RichText,
    Seo: CmsSeo,
    'Spotlighted stores': SpotlightedStores,
    Translation: CmsAccordion$1,
    Video: CmsVideo$1,
    Widget: CmsWidget$1,
    'Recently viewed products': RecentlyViewedProducts
};
function extendComponentDefinitions(definitionsToAdd) {
    return swcHelpers.objectSpread({}, defaultComponentDefinitions, definitionsToAdd);
}

/**
 * This is in a separate module from BloomreachContext itself to avoid a
 * circular dependency between ../component-definitions and ../containers.
 * Do not re-export this from ./index
 */ function BloomreachProvider({ settings , children  }) {
    var ref3;
    const { apiUrl , customComponentMap , componentDefinitions =defaultComponentDefinitions , excludeSkuFromProductUrls , pageModel , visitor , path ,  } = settings;
    var ref1;
    const preview = (ref1 = pageModel === null || pageModel === void 0 ? void 0 : (ref3 = pageModel._meta) === null || ref3 === void 0 ? void 0 : ref3.preview) !== null && ref1 !== void 0 ? ref1 : false;
    const pageSeo = getPageSeo(pageModel);
    const { isMultilingualEnabled  } = useFeatureToggles();
    const sdkConfig = React.useMemo(()=>{
        // Add locale to bloomreach urls for multilingual support (API doesn't accept it in the path)
        const locale = isMultilingualEnabled ? settings.locale : undefined;
        const cmsBaseUrl = createBloomreachApiUrl(apiUrl, locale);
        const parsedPath = createPath(path || '', locale);
        const { origin  } = cmsBaseUrl ? new URL(cmsBaseUrl) : {
            origin: ''
        };
        return {
            cmsBaseUrl,
            visitor,
            origin: origin.replace('www', 'cms'),
            request: {
                path: parsedPath
            },
            httpClient: brHttpClient
        };
    }, [
        apiUrl,
        isMultilingualEnabled,
        settings.locale,
        visitor,
        path
    ]);
    const siteLogos = React.useMemo(()=>{
        var ref;
        var ref2;
        const models = (ref2 = pageModel === null || pageModel === void 0 ? void 0 : (ref = pageModel.page) === null || ref === void 0 ? void 0 : ref.models) !== null && ref2 !== void 0 ? ref2 : {};
        var _logoUrl, _logoSmallUrl, _logoMobileUrl, _logoAlternativeUrl, _reimbursableLogoUrl, _reimbursableLogoAlternativeUrl, _payInstallmentsLogoUrl, _logoFooter;
        return {
            main: (_logoUrl = models.logoUrl) !== null && _logoUrl !== void 0 ? _logoUrl : '',
            small: (_logoSmallUrl = models.logoSmallUrl) !== null && _logoSmallUrl !== void 0 ? _logoSmallUrl : '',
            mobile: (_logoMobileUrl = models.logoMobileUrl) !== null && _logoMobileUrl !== void 0 ? _logoMobileUrl : '',
            alternative: (_logoAlternativeUrl = models.logoAlternativeUrl) !== null && _logoAlternativeUrl !== void 0 ? _logoAlternativeUrl : '',
            reimbursable: (_reimbursableLogoUrl = models.reimbursableLogoUrl) !== null && _reimbursableLogoUrl !== void 0 ? _reimbursableLogoUrl : '',
            reimbursableAlternative: (_reimbursableLogoAlternativeUrl = models.reimbursableLogoAlternativeUrl) !== null && _reimbursableLogoAlternativeUrl !== void 0 ? _reimbursableLogoAlternativeUrl : '',
            payInstallmentsLogo: (_payInstallmentsLogoUrl = models.payInstallmentsLogoUrl) !== null && _payInstallmentsLogoUrl !== void 0 ? _payInstallmentsLogoUrl : '',
            logoFooter: (_logoFooter = models.logoFooter) !== null && _logoFooter !== void 0 ? _logoFooter : ''
        };
    }, [
        pageModel
    ]);
    const value = React.useMemo(()=>swcHelpers.objectSpread({}, initialValue, {
            apiUrl,
            componentDefinitions,
            customComponentMap,
            excludeSkuFromProductUrls,
            pageModel,
            preview,
            path,
            siteLogos,
            sdkConfig,
            pageSeo,
            isMultilingualEnabled
        })
    , [
        apiUrl,
        componentDefinitions,
        customComponentMap,
        excludeSkuFromProductUrls,
        pageModel,
        preview,
        path,
        siteLogos,
        sdkConfig,
        pageSeo,
        isMultilingualEnabled, 
    ]);
    return /*#__PURE__*/ React.createElement(BloomreachContext.Provider, {
        value: value,
        children: children
    });
}

export { index as BloomreachApi, BloomreachProvider, CmsComponentSlot, CmsContainerPage, CmsContentPage, CmsCustomPackage, CmsFooter, CmsGraphProductBreadcrumbs, CmsHeadTags, CmsHeader, CmsPageLayout, CmsProductBreadcrumbs, CmsSeo, RuntimeContext, VISITOR_COOKIE, extendComponentDefinitions, getAlgoliaConfigFromBloomreach, getPageTemplate, getPlpPromotionSize, parsePageModel, useBloomreach, useBloomreachAppointmentContent, useBloomreachAppointmentTypes, useBloomreachCartConfiguration, useBloomreachDeliveryInfo, useBloomreachPageRoutes, useBloomreachPdpContent, useBloomreachProductRoutes, useBloomreachSdpContent };
//# sourceMappingURL=index.js.map
